import { useState, useEffect } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, authFetch } from "../api/client.ts";
import { useAuthStore } from "../store/useAuthStore.ts";
import { Users, Activity, BarChart3, Loader2, RefreshCw, UserPlus, X, KeyRound, BookOpen, Save, Plus, Trash2, Settings, Copy, Wand2, Check, Database, History, Tag, ChevronDown, ChevronUp, ExternalLink, AlertCircle, Pencil, Layers, ThumbsDown, ClipboardCheck, Sparkles } from "lucide-react";
import { COORDINATOR_MANAGEABLE_ROLES } from "@neuroxrehab/types";
import { NoReviewNotice } from "../components/NoReviewNotice.tsx";

// Mirrors ROB_TOOLS in backend/src/routes/risk-of-bias.ts
const QUALITY_TOOL_OPTIONS = [
  { value: "", label: "By review type (QUADAS-2 for assessment, RoB 2 for treatment, Custom otherwise)" },
  { value: "QUADAS2", label: "QUADAS-2 (diagnostic accuracy)" },
  { value: "ROB2", label: "RoB 2 (randomised trials)" },
  { value: "PEDRO", label: "PEDro scale (clinical trials)" },
  { value: "COSMIN", label: "COSMIN (measurement properties)" },
  { value: "CUSTOM", label: "Custom checklist" },
];

interface UserRecord {
  id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  mustChangePassword: boolean;
  expertiseAreas?: string[];
  createdAt: string;
}

interface DuplicateStudySummary {
  id: string;
  title: string;
  authors: string[];
  year: number | null;
  journal: string | null;
  doi: string | null;
  source: string;
}

interface PossibleDuplicate extends DuplicateStudySummary {
  createdAt: string;
  duplicateSimilarity: number;
  possibleDuplicateOf: DuplicateStudySummary;
}

interface AuditEntry {
  id: string;
  userId: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  user?: { name: string } | null;
}

type AdminTab = "users" | "reviews" | "duplicates" | "activity" | "stats" | "settings";

// Role hierarchy — what roles each level can assign
const ROLE_OPTIONS: Record<string, string[]> = {
  ADMIN: ["OBSERVER", "REVIEWER", "SUB_TEAM_LEADER", "COORDINATOR", "ADMIN"],
  COORDINATOR: [...COORDINATOR_MANAGEABLE_ROLES],
};

const ROLE_LABELS: Record<string, string> = {
  OBSERVER: "Observer",
  REVIEWER: "Reviewer",
  SUB_TEAM_LEADER: "Sub-team Leader",
  COORDINATOR: "Coordinator",
  ADMIN: "Admin",
};

export default function AdminPage() {
  const { user: me } = useAuthStore();
  // A sub-team leader manages only their own assigned review(s) - the rest of
  // this page assumes COORDINATOR+, so they get a single cut-down tab instead
  // of a leaky subset of the full admin surface.
  const isSubTeamLeader = me?.role === "SUB_TEAM_LEADER";
  const visibleTabs: AdminTab[] = isSubTeamLeader
    ? ["reviews"]
    : ["users", "reviews", "duplicates", "activity", "stats", "settings"];
  const [tab, setTab] = useState<AdminTab>(isSubTeamLeader ? "reviews" : "users");

  // Fetched here (not inside DuplicatesTab) so the pending count can badge the
  // tab button before a coordinator ever opens it. COORDINATOR+ only on the
  // backend, so skip it entirely for a sub-team leader.
  const { data: dupData } = useQuery({
    queryKey: ["possible-duplicates"],
    queryFn: () => api.get<{ matches: PossibleDuplicate[] }>("/admin/possible-duplicates"),
    enabled: !isSubTeamLeader,
  });
  const pendingDuplicates = dupData?.matches.length ?? 0;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Administration</h1>

      <div className="flex gap-1 mb-6 border rounded-lg p-1 w-fit flex-wrap">
        {visibleTabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === t ? "bg-primary text-primary-foreground" : "hover:bg-accent"
            }`}
          >
            {t === "users" && <Users className="h-4 w-4" />}
            {t === "reviews" && <BookOpen className="h-4 w-4" />}
            {t === "duplicates" && <Layers className="h-4 w-4" />}
            {t === "activity" && <Activity className="h-4 w-4" />}
            {t === "stats" && <BarChart3 className="h-4 w-4" />}
            {t === "settings" && <Settings className="h-4 w-4" />}
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === "duplicates" && pendingDuplicates > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-brand-magenta text-white text-[10px] font-bold leading-none">
                {pendingDuplicates}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "users" && <UsersTab />}
      {tab === "reviews" && <ReviewsTab />}
      {tab === "duplicates" && <DuplicatesTab />}
      {tab === "activity" && <ActivityTab />}
      {tab === "stats" && <StatsTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

function UsersTab() {
  const qc = useQueryClient();
  const { user: me } = useAuthStore();
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "", role: "OBSERVER" });
  const [addError, setAddError] = useState<string | null>(null);
  const [addLoading, setAddLoading] = useState(false);

  // Change-password state (one row at a time)
  const [pwdUserId, setPwdUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdLoading, setPwdLoading] = useState(false);

  // Expertise-tag editor state (one row at a time), feeds the AI reviewer-matching suggestion
  const [expertiseUserId, setExpertiseUserId] = useState<string | null>(null);
  const [expertiseDraft, setExpertiseDraft] = useState<string[]>([]);
  const [expertiseInput, setExpertiseInput] = useState("");
  const [expertiseSaving, setExpertiseSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<{ users: UserRecord[] }>("/users"),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.post(`/users/${id}/deactivate`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      api.patch(`/users/${id}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const handleSaveExpertise = async (userId: string) => {
    setExpertiseSaving(true);
    try {
      await api.patch(`/users/${userId}`, { expertiseAreas: expertiseDraft });
      await qc.invalidateQueries({ queryKey: ["users"] });
      setExpertiseUserId(null);
    } finally {
      setExpertiseSaving(false);
    }
  };

  const addExpertiseTag = () => {
    const tag = expertiseInput.trim();
    if (tag && !expertiseDraft.includes(tag)) setExpertiseDraft((prev) => [...prev, tag]);
    setExpertiseInput("");
  };

  const availableRoles = ROLE_OPTIONS[me?.role ?? ""] ?? [];
  const canManageUser = (targetRole: string) => {
    // Cannot manage users at or above your own role level unless you're ADMIN
    if (me?.role === "ADMIN") return true;
    if (me?.role === "COORDINATOR") return (COORDINATOR_MANAGEABLE_ROLES as readonly string[]).includes(targetRole);
    return false;
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    setAddLoading(true);
    try {
      await api.post("/auth/register", addForm);
      setAddForm({ name: "", email: "", password: "", role: "OBSERVER" });
      setShowAddForm(false);
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (err) {
      setAddError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setAddLoading(false);
    }
  };

  const handleChangePassword = async (userId: string) => {
    if (!newPassword || newPassword.length < 8) {
      setPwdError("Password must be at least 8 characters");
      return;
    }
    setPwdError(null);
    setPwdLoading(true);
    try {
      await api.post(`/users/${userId}/change-password`, { newPassword });
      setPwdUserId(null);
      setNewPassword("");
      // Reflects the "Password change pending" badge the server now sets on the
      // target account (they must set their own password on next login).
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (err) {
      setPwdError(err instanceof Error ? err.message : "Failed to change password");
    } finally {
      setPwdLoading(false);
    }
  };

  const roleMap: Record<string, string> = {
    ADMIN: "bg-purple-100 text-purple-800",
    COORDINATOR: "bg-blue-100 text-blue-800",
    SUB_TEAM_LEADER: "bg-amber-100 text-amber-800",
    REVIEWER: "bg-green-100 text-green-800",
    OBSERVER: "bg-gray-100 text-gray-700",
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex justify-end">
        {availableRoles.length > 0 && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
          >
            {showAddForm ? <X className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            {showAddForm ? "Cancel" : "Add user"}
          </button>
        )}
      </div>

      {/* Add user form */}
      {showAddForm && (
        <form onSubmit={handleAddUser} className="border rounded-lg p-4 bg-secondary/40 space-y-3">
          <h3 className="text-sm font-semibold">New user</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Full name</label>
              <input
                type="text"
                required
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-2.5 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Email</label>
              <input
                type="email"
                required
                value={addForm.email}
                onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full px-2.5 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">
                Password <span className="text-muted-foreground">(min 8 chars, they'll be asked to change it on first login)</span>
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={addForm.password}
                onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full px-2.5 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Role</label>
              <select
                value={addForm.role}
                onChange={(e) => setAddForm((f) => ({ ...f, role: e.target.value }))}
                className="w-full px-2.5 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {availableRoles.map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>
            </div>
          </div>
          {addError && <p className="text-xs text-destructive">{addError}</p>}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={addLoading}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {addLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create user
            </button>
          </div>
        </form>
      )}

      {/* Users table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Name</th>
              <th className="text-left px-4 py-3 font-medium">Email</th>
              <th className="text-left px-4 py-3 font-medium">Role</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {data?.users.map((user) => (
              <>
                <tr key={user.id} className={!user.isActive ? "opacity-50" : ""}>
                  <td className="px-4 py-3">{user.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{user.email}</td>
                  <td className="px-4 py-3">
                    {canManageUser(user.role) && user.id !== me?.id ? (
                      /* Editable role dropdown */
                      <select
                        value={user.role}
                        onChange={(e) => roleMutation.mutate({ id: user.id, role: e.target.value })}
                        className={`px-2 py-0.5 rounded-full text-xs font-medium border-0 cursor-pointer ${roleMap[user.role] ?? ""}`}
                      >
                        {/* Always include current role plus what the editor can set */}
                        {Array.from(new Set([user.role, ...availableRoles])).map((r) => (
                          <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>
                        ))}
                      </select>
                    ) : (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleMap[user.role] ?? ""}`}>
                        {ROLE_LABELS[user.role] ?? user.role}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {user.isActive ? (
                        <span className="text-green-600 text-xs">Active</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">Inactive</span>
                      )}
                      {user.mustChangePassword && (
                        <span
                          className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700"
                          title="This user will be required to set a new password on their next login."
                        >
                          Password change pending
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {/* Change password — self or ADMIN */}
                      {(user.id === me?.id || me?.role === "ADMIN") && (
                        <button
                          onClick={() => {
                            setPwdUserId(pwdUserId === user.id ? null : user.id);
                            setNewPassword("");
                            setPwdError(null);
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                          title="Change password"
                        >
                          <KeyRound className="h-3.5 w-3.5" />
                          {pwdUserId === user.id ? "Cancel" : "Password"}
                        </button>
                      )}
                      {/* Areas of expertise — feeds the AI reviewer-matching suggestion */}
                      {canManageUser(user.role) && (
                        <button
                          onClick={() => {
                            const opening = expertiseUserId !== user.id;
                            setExpertiseUserId(opening ? user.id : null);
                            setExpertiseDraft(opening ? (user.expertiseAreas ?? []) : []);
                            setExpertiseInput("");
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                          title="Areas of expertise"
                        >
                          <Tag className="h-3.5 w-3.5" />
                          {expertiseUserId === user.id ? "Cancel" : "Expertise"}
                        </button>
                      )}
                      {/* Deactivate — ADMIN only, not self */}
                      {me?.role === "ADMIN" && user.isActive && user.id !== me.id && (
                        <button
                          onClick={() => {
                            if (confirm(`Deactivate ${user.name}?`)) deactivateMutation.mutate(user.id);
                          }}
                          className="text-xs text-destructive hover:underline"
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Inline change-password row */}
                {pwdUserId === user.id && (
                  <tr key={`${user.id}-pwd`} className="bg-secondary/30">
                    <td colSpan={5} className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground w-32 shrink-0">New password for <strong>{user.name}</strong></span>
                        <input
                          type="password"
                          placeholder="Min 8 characters"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="flex-1 max-w-xs px-2.5 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          autoFocus
                        />
                        <button
                          onClick={() => handleChangePassword(user.id)}
                          disabled={pwdLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
                        >
                          {pwdLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                          Save
                        </button>
                        {pwdError && <p className="text-xs text-destructive">{pwdError}</p>}
                      </div>
                    </td>
                  </tr>
                )}

                {/* Inline expertise-tag editor row */}
                {expertiseUserId === user.id && (
                  <tr key={`${user.id}-expertise`} className="bg-secondary/30">
                    <td colSpan={5} className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <span className="text-xs text-muted-foreground w-32 shrink-0 pt-1.5">Expertise for <strong>{user.name}</strong></span>
                        <div className="flex-1 max-w-md">
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {expertiseDraft.map((tag) => (
                              <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary border text-xs">
                                {tag}
                                <button onClick={() => setExpertiseDraft((prev) => prev.filter((t) => t !== tag))} className="hover:text-destructive">
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              </span>
                            ))}
                            {expertiseDraft.length === 0 && (
                              <span className="text-xs text-muted-foreground italic">No areas set yet</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={expertiseInput}
                              onChange={(e) => setExpertiseInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExpertiseTag(); } }}
                              placeholder="e.g. eye-tracking, RCT design, XR hardware"
                              className="flex-1 px-2.5 py-1.5 border rounded-md bg-background text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            <button
                              onClick={addExpertiseTag}
                              className="px-2.5 py-1.5 border rounded-md text-xs hover:bg-accent"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={() => void handleSaveExpertise(user.id)}
                          disabled={expertiseSaving}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 disabled:opacity-50 shrink-0"
                        >
                          {expertiseSaving && <Loader2 className="h-3 w-3 animate-spin" />}
                          Save
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Reviews tab ─────────────────────────────────────────────────────────────

type FrameworkData = Record<string, string>;

// Labels for each known framework type — drives the edit form and display table
const FRAMEWORK_CONFIGS: Record<string, Record<string, string>> = {
  PICO: { P: "Population", I: "Intervention", C: "Comparator", O: "Outcomes", S: "Study Design" },
  PIRO: { P: "Population", I: "Index Test", R: "Reference Standard", O: "Outcomes" },
  PCC: { P: "Population", C1: "Concept", C2: "Context" },
};

const FRAMEWORK_OPTIONS = ["PICO", "PIRO", "PCC"] as const;

function buildEmptyFramework(frameworkType: string): FrameworkData {
  const labels = FRAMEWORK_CONFIGS[frameworkType];
  if (!labels) return {};
  return Object.fromEntries(Object.keys(labels).map((k) => [k, ""]));
}

interface ReviewRecord {
  id: string;
  name: string;
  type: string;
  frameworkType: string;
  description: string | null;
  inclusionCriteria: string;
  exclusionCriteria: string;
  additionalConstraints: string | null;
  pico: FrameworkData | null;
  searchString: string | null;
  searchStrings: Record<string, string> | null;
  requiredReviewers: number;
  aiMaxChars: number;
  prosperoId: string | null;
  qualityTool: string | null;
  exclusionCategories: string[];
  inclusionCategories: string[];
  isActive: boolean;
  _count: { studyReviews: number; extractions: number };
}

/** Mirrors backend/src/services/database-search.ts DatabaseSearchResult. */
interface DatabaseSearchResult {
  database: string;
  totalAvailable: number;
  fetched: number;
  imported: number;
  duplicates: number;
  abstractsBackfilled: number;
  stillMissingAbstract: number;
  truncated: boolean;
  newStudyIds: string[];
}

/** Reported by GET /admin/search-providers — which databases this deployment can query. */
interface SearchProviderInfo {
  key: string;
  label: string;
  searchStringKey: string;
  available: boolean;
  unavailableReason: string | null;
}

interface SearchRun {
  id: string;
  reviewId: string;
  database: string;
  searchString: string | null;
  nResults: number;
  nImported: number;
  nDuplicates: number;
  notes: string | null;
  ranBy: string;
  ranAt: string;
  runner: { name: string };
}

interface HistoryEntry {
  id: string;
  reviewId: string;
  changedBy: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  reason: string | null;
  createdAt: string;
  user: { name: string };
}

function PicoTable({ pico, frameworkType }: { pico: FrameworkData; frameworkType: string }) {
  const labels = FRAMEWORK_CONFIGS[frameworkType] ?? {};
  // Order by canonical framework key order, then any extra keys not in config
  const configKeys = Object.keys(labels);
  const extraKeys = Object.keys(pico).filter((k) => !configKeys.includes(k));
  const entries = [...configKeys, ...extraKeys]
    .map((k) => [k, pico[k] ?? ""] as [string, string])
    .filter(([, v]) => v.trim());
  if (entries.length === 0) return null;
  return (
    <div className="border rounded-md overflow-hidden text-xs">
      <table className="w-full">
        <thead className="bg-secondary/60 border-b">
          <tr>
            <th className="text-left px-3 py-2 font-semibold w-8 text-primary">#</th>
            <th className="text-left px-3 py-2 font-semibold w-32">Element</th>
            <th className="text-left px-3 py-2 font-semibold">Definition</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {entries.map(([key, value]) => (
            <tr key={key} className="hover:bg-secondary/20">
              <td className="px-3 py-2.5 font-bold text-primary">{key}</td>
              <td className="px-3 py-2.5 font-medium text-muted-foreground">{labels[key] ?? key}</td>
              <td className="px-3 py-2.5 leading-relaxed">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const KNOWN_REVIEW_TYPES = ["ASSESSMENT", "TREATMENT", "TECHNICAL"] as const;

function DuplicatesTab() {
  const qc = useQueryClient();
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["possible-duplicates"],
    queryFn: () => api.get<{ matches: PossibleDuplicate[] }>("/admin/possible-duplicates"),
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "confirm" | "reject" }) =>
      api.post(`/admin/possible-duplicates/${id}/${action}`, {}),
    onMutate: ({ id }) => setResolvingId(id),
    onSettled: () => {
      setResolvingId(null);
      qc.invalidateQueries({ queryKey: ["possible-duplicates"] });
    },
  });

  const matches = data?.matches ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-base font-semibold mb-1">Possible duplicates</h2>
        <p className="text-sm text-muted-foreground max-w-2xl">
          A close title match on its own can be a real duplicate, or two related but distinct
          papers (a "part 1" / "part 2" series, or an assessment paper and its treatment
          counterpart). Both stay fully visible in every screening queue until a coordinator
          confirms or rejects the match below.
        </p>
      </div>

      {matches.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border rounded-lg bg-card">
          <Layers className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium text-foreground">No possible duplicates awaiting review</p>
        </div>
      ) : (
        <div className="space-y-3">
          {matches.map((m) => (
            <div key={m.id} className="border rounded-lg p-4 bg-card">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-brand-magenta/10 text-brand-magenta">
                  {Math.round(m.duplicateSimilarity * 100)}% title similarity
                </span>
                <span className="text-xs text-muted-foreground">
                  imported {new Date(m.createdAt).toLocaleDateString()}
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <DuplicateStudyCard label="New import" study={m} />
                <DuplicateStudyCard label="Existing study" study={m.possibleDuplicateOf} />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => resolveMutation.mutate({ id: m.id, action: "confirm" })}
                  disabled={resolvingId === m.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-destructive text-destructive-foreground rounded-md text-xs font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {resolvingId === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Layers className="h-3.5 w-3.5" />}
                  It's a duplicate — hide the new import
                </button>
                <button
                  onClick={() => resolveMutation.mutate({ id: m.id, action: "reject" })}
                  disabled={resolvingId === m.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-xs hover:bg-accent disabled:opacity-50"
                >
                  <ThumbsDown className="h-3.5 w-3.5" /> Not a duplicate — keep both
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DuplicateStudyCard({ label, study }: { label: string; study: DuplicateStudySummary }) {
  return (
    <div className="border rounded-md p-3 bg-background">
      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm font-medium leading-snug">{study.title}</p>
      <p className="text-xs text-muted-foreground mt-1">
        {study.authors.slice(0, 2).join(", ")}
        {study.authors.length > 2 ? " et al." : ""}
        {study.year ? ` · ${study.year}` : ""}
        {study.journal ? ` · ${study.journal}` : ""}
      </p>
      {study.doi && <p className="text-[11px] text-muted-foreground mt-1">DOI: {study.doi}</p>}
    </div>
  );
}

interface StudySummary {
  id: string;
  title: string;
  year: number | null;
  status: string;
  studyReviews: { reviewId: string }[];
}

function ReviewsTab() {
  const qc = useQueryClient();
  const { user: me } = useAuthStore();
  const isAdmin = me?.role === "ADMIN";
  const isCoordinator = isAdmin || me?.role === "COORDINATOR";
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "", type: "ASSESSMENT", customType: "",
    inclusionCriteria: "", exclusionCriteria: "",
    description: "", requiredReviewers: 2, aiMaxChars: 60000,
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-reviews"],
    queryFn: () => api.get<{ reviews: ReviewRecord[] }>("/admin/reviews"),
  });

  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const typeValue = createForm.type === "__custom__" ? createForm.customType.trim().toUpperCase() : createForm.type;
      if (!typeValue) { setCreateError("Type is required"); return; }
      await api.post("/admin/reviews", {
        name: createForm.name,
        type: typeValue,
        description: createForm.description || null,
        inclusionCriteria: createForm.inclusionCriteria,
        exclusionCriteria: createForm.exclusionCriteria,
        requiredReviewers: createForm.requiredReviewers,
        aiMaxChars: createForm.aiMaxChars,
      });
      await qc.invalidateQueries({ queryKey: ["admin-reviews"] });
      setShowCreate(false);
      setCreateForm({ name: "", type: "ASSESSMENT", customType: "", inclusionCriteria: "", exclusionCriteria: "", description: "", requiredReviewers: 2, aiMaxChars: 60000 });
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create review");
    } finally {
      setCreating(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  // A sub-team leader sees only their own review(s); with none yet, explain
  // rather than show an empty tab.
  if (!isCoordinator && (data?.reviews.length ?? 0) === 0) return <NoReviewNotice />;

  return (
    <div className="space-y-6">
      {/* Create new review */}
      {isCoordinator && (
        <div className="border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-secondary/40 border-b">
            <span className="text-sm font-semibold">Reviews</span>
            <button
              onClick={() => setShowCreate(!showCreate)}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent"
            >
              <Plus className="h-3 w-3" /> {showCreate ? "Cancel" : "New review"}
            </button>
          </div>
          {showCreate && (
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-0.5">Name</label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Cognitive Rehabilitation Review"
                    className="w-full px-2 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-0.5">Review type</label>
                  <select
                    value={createForm.type}
                    onChange={(e) => setCreateForm((f) => ({ ...f, type: e.target.value }))}
                    className="w-full px-2 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {KNOWN_REVIEW_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    <option value="__custom__">Custom type…</option>
                  </select>
                  {createForm.type === "__custom__" && (
                    <input
                      type="text"
                      value={createForm.customType}
                      onChange={(e) => setCreateForm((f) => ({ ...f, customType: e.target.value }))}
                      placeholder="e.g. PREVENTION"
                      className="mt-1 w-full px-2 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  )}
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Known types (ASSESSMENT, TREATMENT, TECHNICAL) have full extraction schemas. Custom types use a generic editor.
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-0.5">Description (optional)</label>
                <input
                  type="text"
                  value={createForm.description}
                  onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full px-2 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <CriteriaField label="Inclusion criteria" value={createForm.inclusionCriteria} onChange={(v) => setCreateForm((f) => ({ ...f, inclusionCriteria: v }))} rows={4} />
              <CriteriaField label="Exclusion criteria" value={createForm.exclusionCriteria} onChange={(v) => setCreateForm((f) => ({ ...f, exclusionCriteria: v }))} rows={4} />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-0.5">Required reviewer decisions</label>
                  <input type="number" min={1} max={10} value={createForm.requiredReviewers}
                    onChange={(e) => setCreateForm((f) => ({ ...f, requiredReviewers: Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)) }))}
                    className="w-20 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-0.5">AI character limit</label>
                  <input type="number" min={1000} max={500000} step={1000} value={createForm.aiMaxChars}
                    onChange={(e) => setCreateForm((f) => ({ ...f, aiMaxChars: Math.max(1000, parseInt(e.target.value, 10) || 60000) }))}
                    className="w-32 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Characters of paper text sent to AI (default: 60,000)</p>
                </div>
              </div>
              {createError && <p className="text-xs text-destructive">{createError}</p>}
              <button
                onClick={handleCreate}
                disabled={creating || !createForm.name}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
              >
                {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                Create review
              </button>
            </div>
          )}
        </div>
      )}

      {data?.reviews.map((review) => (
        <ReviewCard key={review.id} review={review} isAdmin={isAdmin} onSaved={() => qc.invalidateQueries({ queryKey: ["admin-reviews"] })} />
      ))}
    </div>
  );
}

function ReviewCard({ review, isAdmin, onSaved }: { review: ReviewRecord; isAdmin: boolean; onSaved: () => void }) {
  const qc = useQueryClient();
  const { user: me } = useAuthStore();
  const isCoordinator = me?.role === "COORDINATOR" || me?.role === "ADMIN";
  // Reviewer-allocation management: COORDINATOR/ADMIN for any review, or a
  // SUB_TEAM_LEADER - who only ever sees their own assigned review(s) here,
  // since GET /admin/reviews already scopes the list server-side for them.
  const canManageReviewers = isCoordinator || me?.role === "SUB_TEAM_LEADER";

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    inclusionCriteria: review.inclusionCriteria,
    exclusionCriteria: review.exclusionCriteria,
    additionalConstraints: review.additionalConstraints ?? "",
    requiredReviewers: review.requiredReviewers,
    aiMaxChars: review.aiMaxChars ?? 60000,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Framework editing state (COORDINATOR+)
  const [editingPico, setEditingPico] = useState(false);
  const [frameworkType, setFrameworkType] = useState(review.frameworkType ?? "PICO");
  const [picoForm, setPicoForm] = useState<FrameworkData>(
    review.pico ?? buildEmptyFramework(review.frameworkType ?? "PICO")
  );
  const [picoSaving, setPicoSaving] = useState(false);
  const [picoError, setPicoError] = useState<string | null>(null);
  const [searchString, setSearchString] = useState(review.searchString ?? "");
  const [searchStrings, setSearchStrings] = useState<Record<string, string>>(review.searchStrings ?? {});
  const [generatingSearch, setGeneratingSearch] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [searchCopied, setSearchCopied] = useState(false);
  const [targetDb, setTargetDb] = useState("pubmed");
  const [adaptedString, setAdaptedString] = useState<string | null>(null);
  const [adaptingSearch, setAdaptingSearch] = useState(false);
  const [editingSearch, setEditingSearch] = useState(false);
  const [editDraft, setEditDraft] = useState("");
  const [savingSearchEdit, setSavingSearchEdit] = useState(false);
  const [providers, setProviders] = useState<SearchProviderInfo[]>([]);
  const [runningSearch, setRunningSearch] = useState(false);
  const [searchRunNotice, setSearchRunNotice] = useState<string | null>(null);
  const [searchRunError, setSearchRunError] = useState<string | null>(null);
  const [searchRunProgress, setSearchRunProgress] = useState<{ fetched: number; total: number } | null>(null);

  // Which databases can be searched over an API varies with deployment (some
  // need a vendor key), so the UI asks the server rather than hardcoding it.
  useEffect(() => {
    api
      .get<{ providers: SearchProviderInfo[] }>("/admin/search-providers")
      .then((r) => setProviders(r.providers))
      .catch(() => { /* button simply stays disabled */ });
  }, []);

  const currentProvider = providers.find((p) => p.searchStringKey === targetDb) ?? null;

  const [showAssign, setShowAssign] = useState(false);
  const [showSearchRuns, setShowSearchRuns] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showReviewers, setShowReviewers] = useState(false);
  const [showAllocations, setShowAllocations] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [prosperoId, setProsperoId] = useState(review.prosperoId ?? "");
  const [amendmentReason, setAmendmentReason] = useState("");

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/admin/reviews/${review.id}`, {
        inclusionCriteria: form.inclusionCriteria,
        exclusionCriteria: form.exclusionCriteria,
        additionalConstraints: form.additionalConstraints || null,
        requiredReviewers: form.requiredReviewers,
        aiMaxChars: form.aiMaxChars,
        prosperoId: prosperoId.trim() || null,
        amendmentReason: amendmentReason.trim() || undefined,
      });
      onSaved();
      setEditing(false);
      setAmendmentReason("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePico = async () => {
    setPicoSaving(true);
    setPicoError(null);
    try {
      const framework = Object.fromEntries(
        Object.entries(picoForm).filter(([, v]) => v.trim())
      );
      await api.patch(`/admin/reviews/${review.id}/pico`, {
        framework: Object.keys(framework).length ? framework : null,
        frameworkType,
        searchString: searchString.trim() || null,
        requiredReviewers: form.requiredReviewers,
        aiMaxChars: form.aiMaxChars,
      });
      onSaved();
      setEditingPico(false);
    } catch (err) {
      setPicoError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setPicoSaving(false);
    }
  };

  const runGenerateSearch = async () => {
    setGeneratingSearch(true);
    setPicoError(null);
    try {
      const result = await api.post<{ searchString: string; searchStrings: Record<string, string> }>(
        `/admin/reviews/${review.id}/generate-search-string`,
        {}
      );
      setSearchString(result.searchString);
      setSearchStrings(result.searchStrings ?? {});
      setAdaptedString(null);
      setTargetDb("pubmed");
      setEditingSearch(false);
      onSaved();
    } catch (err) {
      setPicoError(err instanceof Error ? err.message : "Failed to generate search string");
    } finally {
      setGeneratingSearch(false);
    }
  };

  const handleGenerateSearch = () => {
    // Regenerating replaces existing strings (including manual edits), so
    // confirm first -- as an in-app modal rather than a native browser
    // confirm(), which looks like an untrusted "this site says" popup and
    // sits outside the app's own visual language.
    if (searchString) {
      setShowRegenerateConfirm(true);
      return;
    }
    void runGenerateSearch();
  };

  const startEditingSearch = () => {
    // Prime the textarea with whatever is currently displayed
    const current = targetDb === "pubmed" ? searchString : (adaptedString ?? searchStrings[targetDb] ?? searchString);
    setEditDraft(current);
    setEditingSearch(true);
  };

  const cancelEditSearch = () => {
    setEditingSearch(false);
    setEditDraft("");
  };

  const saveSearchEdit = async () => {
    setSavingSearchEdit(true);
    try {
      await api.patch(`/admin/reviews/${review.id}/search-strings`, { db: targetDb, value: editDraft });
      // Update local state immediately
      if (targetDb === "pubmed") {
        setSearchString(editDraft);
      } else {
        setAdaptedString(editDraft);
      }
      setSearchStrings((prev) => ({ ...prev, [targetDb]: editDraft }));
      setEditingSearch(false);
      onSaved();
    } catch (err) {
      setPicoError(err instanceof Error ? err.message : "Failed to save edit");
    } finally {
      setSavingSearchEdit(false);
    }
  };

  // Polls until the background search finishes, instead of leaving the
  // coordinator with only "started" and a wait for email — which silently
  // never arrives outside the developer's own address on this deployment's
  // Resend sandbox. Every completed or failed run is also logged to
  // SearchRun, so the "Search runs" panel is refreshed once done too.
  const pollRunSearch = async (jobId: string) => {
    const POLL_INTERVAL_MS = 2000;
    const MAX_POLLS = 150; // 5 minutes; a broad search over thousands of records can take a while

    // Every query that could show stale counts after an import: this
    // review's card, the admin review list, and the dashboard stat cards.
    // Dashboard queries only refetch once mounted again if inactive right
    // now, which is fine -- they'll be fresh whenever it's next opened.
    const refreshStats = () => {
      qc.invalidateQueries({ queryKey: ["search-runs", review.id] });
      qc.invalidateQueries({ queryKey: ["admin-reviews"] });
      qc.invalidateQueries({ queryKey: ["review-stats"] });
      qc.invalidateQueries({ queryKey: ["study-stats"] });
    };

    for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      const status = await api.get<
        | { state: "not_found" }
        | { state: "pending"; progress: { fetched: number; total: number } | null }
        | { state: "completed"; result: DatabaseSearchResult }
        | { state: "failed"; error: string }
      >(`/admin/reviews/${review.id}/run-search/${jobId}`);

      if (status.state === "completed") {
        const r = status.result;
        setSearchRunNotice(
          `Found ${r.totalAvailable.toLocaleString()} record${r.totalAvailable === 1 ? "" : "s"}` +
            (r.truncated ? ` (fetched ${r.fetched.toLocaleString()}, capped)` : "") +
            `. Imported ${r.imported.toLocaleString()} new, skipped ${r.duplicates.toLocaleString()} duplicate${r.duplicates === 1 ? "" : "s"}` +
            (r.abstractsBackfilled > 0 ? `, recovered ${r.abstractsBackfilled} abstract${r.abstractsBackfilled === 1 ? "" : "s"} from DOI` : "") +
            (r.stillMissingAbstract > 0 ? `. ${r.stillMissingAbstract} record${r.stillMissingAbstract === 1 ? "" : "s"} still have no abstract` : "") +
            "."
        );
        setSearchRunProgress(null);
        refreshStats();
        return;
      }
      if (status.state === "failed") {
        setSearchRunError(status.error);
        setSearchRunProgress(null);
        refreshStats();
        return;
      }
      if (status.state === "not_found") {
        setSearchRunError("Lost track of the search job. Check the Search runs log below for the outcome.");
        setSearchRunProgress(null);
        return;
      }
      // still pending: show live progress once the fetch phase has started
      if (status.progress && status.progress.total > 0) {
        setSearchRunProgress(status.progress);
      }
    }

    setSearchRunNotice("Still running after 5 minutes. Check the Search runs log below once it finishes.");
    setSearchRunProgress(null);
  };

  const handleRunSearch = async () => {
    if (!currentProvider) return;
    setRunningSearch(true);
    setSearchRunNotice(null);
    setSearchRunError(null);
    setSearchRunProgress(null);
    try {
      const result = await api.post<{ message: string; jobId: string }>(
        `/admin/reviews/${review.id}/run-search`,
        { database: currentProvider.key }
      );
      setSearchRunNotice(`${result.message} Waiting for results…`);
      await pollRunSearch(result.jobId);
    } catch (err) {
      setSearchRunError(err instanceof Error ? err.message : "Could not start the search");
    } finally {
      setRunningSearch(false);
    }
  };

  const handleCopySearch = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setSearchCopied(true);
    setTimeout(() => setSearchCopied(false), 2000);
  };

  const handleDbChange = async (db: string) => {
    setTargetDb(db);
    setAdaptedString(null);
    setEditingSearch(false); // exit edit mode when switching databases
    if (db === "pubmed") return;
    // Use pre-generated variant if available (generated alongside PubMed string)
    if (searchStrings[db]) {
      setAdaptedString(searchStrings[db]);
      return;
    }
    // Fallback: call adapt endpoint for reviews generated before this feature
    const source = review.searchString;
    if (!source) return;
    setAdaptingSearch(true);
    try {
      const result = await api.post<{ adaptedString: string }>(
        `/admin/reviews/${review.id}/adapt-search-string`,
        { targetDatabase: db }
      );
      setAdaptedString(result.adaptedString);
      // Cache it locally so subsequent selections don't re-call
      setSearchStrings((prev) => ({ ...prev, [db]: result.adaptedString }));
    } catch {
      setAdaptedString("Adaptation failed. Please try again.");
    } finally {
      setAdaptingSearch(false);
    }
  };

  const handleDelete = async (force = false) => {
    setDeleting(true);
    try {
      const url = `/admin/reviews/${review.id}${force ? "?force=true" : ""}`;
      const res = await authFetch(`/api${url}`, { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json() as { error?: string; studyCount?: number };
        if (res.status === 409 && body.studyCount) {
          if (window.confirm(`This review has ${body.studyCount} assigned studies. All screening decisions and extractions will also be deleted. Proceed?`)) {
            await handleDelete(true);
          }
          return;
        }
        throw new Error(body.error ?? "Delete failed");
      }
      await qc.invalidateQueries({ queryKey: ["admin-reviews"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const typeColors: Record<string, string> = {
    ASSESSMENT: "bg-blue-100 text-blue-800",
    TREATMENT: "bg-green-100 text-green-800",
    TECHNICAL: "bg-purple-100 text-purple-800",
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-secondary/40 border-b">
        <div className="flex items-center gap-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColors[review.type] ?? ""}`}>
            {review.type}
          </span>
          <h3 className="font-semibold text-sm">{review.name}</h3>
          <span className="text-xs text-muted-foreground">
            {review._count.studyReviews} studies · {review._count.extractions} extractions
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary border font-medium" title="Required reviewer decisions before a study advances">
            {review.requiredReviewers} reviewer{review.requiredReviewers !== 1 ? "s" : ""} required
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary border font-medium" title="Max characters of text sent to AI per request">
            AI: {((review.aiMaxChars ?? 60000) / 1000).toFixed(0)}k chars
          </span>
          <QualityToolControl reviewId={review.id} value={review.qualityTool} canEdit={canManageReviewers} onSaved={onSaved} />
          {review.prosperoId && (
            <a
              href={`https://www.crd.york.ac.uk/prospero/display_record.php?ID=${review.prosperoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200 font-medium hover:bg-blue-200 transition-colors"
              title="View PROSPERO registration"
            >
              PROSPERO <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowAssign(!showAssign)}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent"
          >
            <Plus className="h-3 w-3" /> Assign studies
          </button>
          <button
            onClick={() => setShowSearchRuns(!showSearchRuns)}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent ${showSearchRuns ? "bg-accent" : ""}`}
          >
            <Database className="h-3 w-3" /> Search runs
          </button>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent ${showHistory ? "bg-accent" : ""}`}
          >
            <History className="h-3 w-3" /> Protocol history
          </button>
          {canManageReviewers && (
            <button
              onClick={() => setShowReviewers(!showReviewers)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent ${showReviewers ? "bg-accent" : ""}`}
            >
              <Users className="h-3 w-3" /> Reviewers
            </button>
          )}
          {canManageReviewers && (
            <button
              onClick={() => setShowAllocations(!showAllocations)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent ${showAllocations ? "bg-accent" : ""}`}
            >
              <ClipboardCheck className="h-3 w-3" /> Allocate studies
            </button>
          )}
          {isCoordinator && (
            <button
              onClick={() => { setEditingPico(!editingPico); setPicoError(null); }}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent"
            >
              {editingPico ? <X className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}
              {editingPico ? "Cancel" : "Edit framework"}
            </button>
          )}
          {isCoordinator && (
            <button
              onClick={() => { setEditing(!editing); setError(null); setConfirmDelete(false); }}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent"
            >
              {editing ? <X className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}
              {editing ? "Cancel" : "Edit criteria"}
            </button>
          )}
          {isAdmin && (
            confirmDelete ? (
              <div className="flex items-center gap-1">
                <span className="text-xs text-destructive">Delete "{review.name}"?</span>
                <button onClick={() => void handleDelete()} disabled={deleting}
                  className="text-xs px-2 py-1 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 disabled:opacity-50">
                  {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yes, delete"}
                </button>
                <button onClick={() => setConfirmDelete(false)} className="text-xs px-2 py-1 border rounded-md hover:bg-accent">Cancel</button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            )
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Research framework — always visible; editable by COORDINATOR+ */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
            {review.frameworkType} Framework
            <span className="ml-2 text-[9px] font-normal normal-case text-muted-foreground/60">
              Included in AI screening prompt
            </span>
          </p>

          {editingPico ? (
            <div className="space-y-3">
              {/* Framework type selector */}
              <div className="flex items-center gap-3">
                <label className="text-xs font-medium shrink-0">Framework type</label>
                <select
                  value={frameworkType}
                  onChange={(e) => {
                    const ft = e.target.value;
                    setFrameworkType(ft);
                    // Reset form to new framework's keys (preserve values where keys overlap)
                    const emptyNew = buildEmptyFramework(ft);
                    setPicoForm((prev) =>
                      Object.fromEntries(
                        Object.keys(emptyNew).map((k) => [k, prev[k] ?? ""])
                      )
                    );
                  }}
                  className="px-2 py-1 border rounded-md bg-background text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {FRAMEWORK_OPTIONS.map((ft) => (
                    <option key={ft} value={ft}>{ft}</option>
                  ))}
                  {!FRAMEWORK_OPTIONS.includes(frameworkType as typeof FRAMEWORK_OPTIONS[number]) && (
                    <option value={frameworkType}>{frameworkType} (custom)</option>
                  )}
                </select>
                <span className="text-[10px] text-muted-foreground">
                  {frameworkType === "PICO" && "Population · Intervention · Comparator · Outcomes · Study Design"}
                  {frameworkType === "PIRO" && "Population · Index Test · Reference Standard · Outcomes"}
                  {frameworkType === "PCC" && "Population · Concept · Context"}
                </span>
              </div>

              {/* Framework fields */}
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/60 border-b">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold w-8 text-primary">#</th>
                      <th className="text-left px-3 py-2 font-semibold w-32">Element</th>
                      <th className="text-left px-3 py-2 font-semibold">Definition</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {Object.entries(FRAMEWORK_CONFIGS[frameworkType] ?? {}).map(([key, label]) => (
                      <tr key={key}>
                        <td className="px-3 py-2 font-bold text-primary align-top pt-3">{key}</td>
                        <td className="px-3 py-2 font-medium text-muted-foreground align-top pt-3">{label}</td>
                        <td className="px-3 py-2">
                          <textarea
                            rows={2}
                            value={picoForm[key] ?? ""}
                            onChange={(e) => setPicoForm((f) => ({ ...f, [key]: e.target.value }))}
                            className="w-full px-2 py-1.5 border rounded bg-background text-xs font-sans focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Screening settings — shown here so COORDINATORs can adjust without needing ADMIN */}
              <div className="border-t pt-3 mt-1 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-0.5">Reviewers required per study</label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    <strong>1</strong> = solo / testing &nbsp;·&nbsp; <strong>2</strong> = standard dual-review
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={1} max={10}
                      value={form.requiredReviewers}
                      onChange={(e) => setForm((f) => ({ ...f, requiredReviewers: Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)) }))}
                      className="w-20 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <span className="text-xs text-muted-foreground">
                      {form.requiredReviewers === 1 ? "Single-reviewer mode" : `${form.requiredReviewers} unanimous decisions required`}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-0.5">AI character limit</label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Max chars of paper text sent to AI. Default: 60,000 (~45 pages).
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={1000} max={500000} step={5000}
                      value={form.aiMaxChars}
                      onChange={(e) => setForm((f) => ({ ...f, aiMaxChars: Math.max(1000, parseInt(e.target.value, 10) || 60000) }))}
                      className="w-28 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <span className="text-xs text-muted-foreground">≈ {Math.round(form.aiMaxChars / 1333).toLocaleString()} pages</span>
                  </div>
                </div>
              </div>

              {picoError && <p className="text-xs text-destructive">{picoError}</p>}
              <button
                onClick={handleSavePico}
                disabled={picoSaving}
                className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
              >
                {picoSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Save framework &amp; settings
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {review.pico && Object.values(review.pico).some((v) => v) ? (
                <PicoTable pico={review.pico} frameworkType={review.frameworkType ?? "PICO"} />
              ) : (
                <p className="text-xs text-muted-foreground italic border rounded-md px-3 py-2">
                  No framework data defined yet.{isCoordinator ? ' Click "Edit framework" to add.' : ""}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Criteria — view or edit mode */}
        {editing ? (
          <div className="space-y-3">
            <CriteriaField
              label="Inclusion criteria"
              value={form.inclusionCriteria}
              onChange={(v) => setForm((f) => ({ ...f, inclusionCriteria: v }))}
            />
            <CriteriaField
              label="Exclusion criteria"
              value={form.exclusionCriteria}
              onChange={(v) => setForm((f) => ({ ...f, exclusionCriteria: v }))}
            />
            <CriteriaField
              label="Additional constraints / AI context"
              hint="Appended to every AI screening prompt for this review. Use this to add PICO refinements, specific device constraints, or any additional context."
              value={form.additionalConstraints}
              onChange={(v) => setForm((f) => ({ ...f, additionalConstraints: v }))}
              rows={4}
            />
            <div className="flex items-start gap-8 pt-1 flex-wrap">
              <div>
                <label className="block text-xs font-medium mb-0.5">Required reviewer decisions</label>
                <p className="text-xs text-muted-foreground mb-1">
                  Human decisions needed before a study advances.
                  <strong> 1</strong> = solo; <strong>2</strong> = standard dual-review.
                </p>
                <div className="flex items-center gap-2">
                  <input type="number" min={1} max={10} value={form.requiredReviewers}
                    onChange={(e) => setForm((f) => ({ ...f, requiredReviewers: Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)) }))}
                    className="w-20 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <span className="text-xs text-muted-foreground">
                    {form.requiredReviewers === 1 ? "Single-reviewer mode" : `${form.requiredReviewers} unanimous decisions required`}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-0.5">AI character limit</label>
                <p className="text-xs text-muted-foreground mb-1">
                  Max characters of paper text passed to the AI per request. Higher = more context, higher cost. Default: 60,000 (~45 pages).
                </p>
                <div className="flex items-center gap-2">
                  <input type="number" min={1000} max={500000} step={5000} value={form.aiMaxChars}
                    onChange={(e) => setForm((f) => ({ ...f, aiMaxChars: Math.max(1000, parseInt(e.target.value, 10) || 60000) }))}
                    className="w-28 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <span className="text-xs text-muted-foreground">
                    ≈ {Math.round(form.aiMaxChars / 1333).toLocaleString()} pages
                  </span>
                </div>
              </div>
            </div>
            <div className="border-t pt-3 mt-3">
              <label className="block text-xs font-medium mb-0.5">PROSPERO registration ID</label>
              <p className="text-xs text-muted-foreground mb-1">
                If this review is registered on PROSPERO, enter the ID (e.g. CRD42024123456). Shown as a badge in the review header.
              </p>
              <input
                type="text"
                value={prosperoId}
                onChange={(e) => setProsperoId(e.target.value)}
                placeholder="CRD42024123456"
                className="w-52 px-2 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-0.5">Amendment reason <span className="text-muted-foreground font-normal">(optional)</span></label>
              <p className="text-xs text-muted-foreground mb-1">
                If you are changing inclusion/exclusion criteria or the search string, document why. This is logged in the Protocol History for PRISMA transparency.
              </p>
              <input
                type="text"
                value={amendmentReason}
                onChange={(e) => setAmendmentReason(e.target.value)}
                placeholder="e.g. Broadened population criteria after pilot screening identified too few records"
                className="w-full px-2 py-1.5 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save criteria
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4 text-xs">
            <div>
              <p className="font-medium text-muted-foreground mb-1 uppercase tracking-wide text-[10px]">Inclusion</p>
              <pre className="whitespace-pre-wrap font-sans leading-relaxed">{review.inclusionCriteria}</pre>
            </div>
            <div>
              <p className="font-medium text-muted-foreground mb-1 uppercase tracking-wide text-[10px]">Exclusion</p>
              <pre className="whitespace-pre-wrap font-sans leading-relaxed">{review.exclusionCriteria}</pre>
            </div>
            <div>
              <p className="font-medium text-muted-foreground mb-1 uppercase tracking-wide text-[10px]">AI constraints</p>
              {review.additionalConstraints ? (
                <pre className="whitespace-pre-wrap font-sans leading-relaxed">{review.additionalConstraints}</pre>
              ) : (
                <p className="text-muted-foreground italic">None. Click "Edit criteria" to add.</p>
              )}
            </div>
          </div>
        )}

        {/* Recommended search string — its own section below Criteria, since generating
            it depends on the framework and criteria above being filled in first */}
        {(searchString || isCoordinator) && (
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Recommended search string
              </p>
              {isCoordinator && (
                <button
                  onClick={handleGenerateSearch}
                  disabled={generatingSearch}
                  className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 border rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  title={
                    searchString
                      ? "Regenerate all database variants from the current framework data and criteria (replaces existing strings, including manual edits)"
                      : "Generate from AI using the framework and criteria above"
                  }
                >
                  {generatingSearch ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Wand2 className="h-2.5 w-2.5" />}
                  {generatingSearch ? "Generating…" : searchString ? "Regenerate with AI" : "Generate with AI"}
                </button>
              )}
              {searchString && (
                <>
                  <select
                    value={targetDb}
                    onChange={(e) => handleDbChange(e.target.value)}
                    className="text-[10px] border rounded px-1.5 py-0.5 bg-background text-foreground"
                  >
                    <option value="pubmed">PubMed / MEDLINE</option>
                    <option value="europepmc">Europe PMC</option>
                    <option value="openalex">OpenAlex (incl. ACM, IEEE, CHI)</option>
                    <option value="wos">Web of Science</option>
                    <option value="scopus">Scopus</option>
                    <option value="cinahl">CINAHL</option>
                    <option value="embase">Embase (Ovid)</option>
                    <option value="psycinfo">PsycINFO</option>
                    <option value="ieee">IEEE Xplore</option>
                  </select>
                  {adaptingSearch && (
                    <span className="text-[10px] text-muted-foreground animate-pulse">Adapting…</span>
                  )}
                  <button
                    onClick={() => handleCopySearch(adaptedString ?? searchString)}
                    disabled={adaptingSearch || editingSearch}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 border rounded disabled:opacity-40"
                  >
                    {searchCopied ? <Check className="h-2.5 w-2.5 text-green-600" /> : <Copy className="h-2.5 w-2.5" />}
                    {searchCopied ? "Copied!" : "Copy"}
                  </button>
                  {isCoordinator && !editingSearch && (
                    <button
                      onClick={startEditingSearch}
                      disabled={adaptingSearch}
                      className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 border rounded disabled:opacity-40"
                      title="Edit this string manually"
                    >
                      <Pencil className="h-2.5 w-2.5" /> Edit
                    </button>
                  )}
                  {isCoordinator && !editingSearch && (
                    <button
                      onClick={handleRunSearch}
                      disabled={adaptingSearch || runningSearch || !currentProvider?.available}
                      className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 border rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:bg-muted disabled:text-muted-foreground"
                      title={
                        currentProvider?.available
                          ? `Run this search against ${currentProvider.label} and import the results`
                          : currentProvider?.unavailableReason ??
                            "This database cannot be searched automatically. Export results and import the file instead."
                      }
                    >
                      {runningSearch
                        ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        : <Database className="h-2.5 w-2.5" />}
                      {runningSearch ? "Running…" : "Fetch results"}
                    </button>
                  )}
                </>
              )}
            </div>

            {searchRunProgress && searchRunProgress.total > 0 && (
              <div className="mb-2">
                <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{
                      width: `${Math.min(100, Math.round((searchRunProgress.fetched / searchRunProgress.total) * 100))}%`,
                    }}
                  />
                </div>
              </div>
            )}
            {searchRunNotice && (
              <div className="mb-2 flex items-start gap-2 text-[11px] rounded-md border border-green-600/30 bg-green-600/10 px-2.5 py-1.5 text-green-800 dark:text-green-300">
                {runningSearch
                  ? <Loader2 className="h-3 w-3 mt-0.5 shrink-0 animate-spin" />
                  : <Check className="h-3 w-3 mt-0.5 shrink-0" />}
                <span>{searchRunNotice}</span>
              </div>
            )}
            {searchRunError && (
              <div className="mb-2 flex items-start gap-2 text-[11px] rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1.5 text-destructive">
                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{searchRunError}</span>
              </div>
            )}
            {picoError && (
              <div className="mb-2 flex items-start gap-2 text-[11px] rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1.5 text-destructive">
                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{picoError}</span>
              </div>
            )}
            {currentProvider && !currentProvider.available && !editingSearch && (
              <p className="mb-2 text-[10px] text-muted-foreground italic">
                {currentProvider.unavailableReason}
              </p>
            )}

            {searchString ? (
              <>
                {editingSearch ? (
                  <div className="space-y-2">
                    <textarea
                      className="w-full text-xs font-mono border rounded-md px-3 py-2 leading-relaxed bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                      rows={12}
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={saveSearchEdit}
                        disabled={savingSearchEdit}
                        className="flex items-center gap-1 text-xs px-3 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
                      >
                        {savingSearchEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                        Save
                      </button>
                      <button
                        onClick={cancelEditSearch}
                        disabled={savingSearchEdit}
                        className="text-xs px-3 py-1 border rounded hover:bg-accent disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <span className="text-[10px] text-muted-foreground">
                        Editing {targetDb === "pubmed" ? "PubMed / MEDLINE" : targetDb.toUpperCase()} variant (other databases unchanged)
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-secondary/40 border rounded-md px-3 py-2 leading-relaxed">
                      {adaptingSearch ? "Generating adapted syntax…" : (adaptedString ?? searchString)}
                    </pre>
                    {targetDb !== "pubmed" && !adaptingSearch && (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Pre-generated for this database: switches are instant, no extra AI call needed.
                      </p>
                    )}
                  </>
                )}
              </>
            ) : (
              <p className="text-xs text-muted-foreground italic border rounded-md px-3 py-2">
                No search string yet.{isCoordinator ? ' Fill in the framework and criteria above, then click "Generate with AI".' : ""}
              </p>
            )}
          </div>
        )}

        {/* Study assignment panel */}
        {showAssign && (
          <StudyAssignPanel reviewId={review.id} onAssigned={() => qc.invalidateQueries({ queryKey: ["admin-reviews"] })} />
        )}

        {/* Search run log */}
        {showSearchRuns && (
          <SearchRunsPanel reviewId={review.id} isCoordinator={isCoordinator} />
        )}

        {/* Protocol version history */}
        {showHistory && (
          <ProtocolHistoryPanel reviewId={review.id} />
        )}

        {/* Reviewer assignment */}
        {showReviewers && (
          <ReviewAssignmentPanel reviewId={review.id} />
        )}

        {/* Per-study allocation */}
        {showAllocations && (
          <StudyAllocationPanel reviewId={review.id} />
        )}

        {/* Structured inclusion/exclusion checklists — shown as checkboxes when screening */}
        {isCoordinator && (
          <>
            <CriteriaCategoriesPanel reviewId={review.id} reviewType={review.type} kind="inclusion" initialCategories={review.inclusionCategories ?? []} onSaved={onSaved} />
            <CriteriaCategoriesPanel reviewId={review.id} reviewType={review.type} kind="exclusion" initialCategories={review.exclusionCategories ?? []} onSaved={onSaved} />
          </>
        )}
      </div>

      {showRegenerateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md bg-card border rounded-xl shadow-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <Wand2 className="h-5 w-5 text-primary shrink-0" />
              <h3 className="font-semibold text-base">Regenerate search strings?</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              This replaces all database variants for "{review.name}", including any manual edits, with a fresh set based on the current framework and criteria.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowRegenerateConfirm(false)}
                className="text-xs px-3 py-1.5 border rounded-md hover:bg-accent"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowRegenerateConfirm(false); void runGenerateSearch(); }}
                className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CriteriaField({
  label, hint, value, onChange, rows = 6,
}: {
  label: string; hint?: string; value: string; onChange: (v: string) => void; rows?: number;
}) {
  return (
    <div>
      <label className="block text-xs font-medium mb-0.5">{label}</label>
      {hint && <p className="text-xs text-muted-foreground mb-1">{hint}</p>}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 border rounded-md bg-background text-xs font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
      />
    </div>
  );
}

function StudyAssignPanel({ reviewId, onAssigned }: { reviewId: string; onAssigned: () => void }) {
  const [search, setSearch] = useState("");
  const [assigning, setAssigning] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["studies-for-assign", search],
    queryFn: () =>
      api.get<{ studies: StudySummary[]; pagination: { total: number } }>(
        `/studies?limit=20&page=1${search ? `&search=${encodeURIComponent(search)}` : ""}`
      ),
    enabled: true,
  });

  const handleAssign = async (studyId: string) => {
    setAssigning(studyId);
    setMessage(null);
    try {
      await api.post(`/studies/${studyId}/assign-review`, { reviewId });
      setMessage("Assigned successfully");
      onAssigned();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to assign");
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div className="border rounded-md p-3 bg-background space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-xs font-semibold">Assign studies to this review</p>
        <p className="text-xs text-muted-foreground">Search and click to assign</p>
        {message && <p className="text-xs text-green-700 ml-auto">{message}</p>}
      </div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by title…"
        className="w-full px-2.5 py-1.5 border rounded-md text-xs bg-background focus:outline-none focus:ring-2 focus:ring-ring"
      />
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mx-auto" />
      ) : (
        <div className="divide-y max-h-60 overflow-y-auto text-xs">
          {data?.studies.map((s) => {
            const assigned = s.studyReviews?.some((sr) => sr.reviewId === reviewId);
            return (
              <div key={s.id} className="flex items-center justify-between py-2 gap-2">
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{s.title}</p>
                  <p className="text-muted-foreground">{s.year} · {s.status}</p>
                </div>
                {assigned ? (
                  <span className="text-green-700 shrink-0">Assigned</span>
                ) : (
                  <button
                    onClick={() => handleAssign(s.id)}
                    disabled={assigning === s.id}
                    className="flex items-center gap-1 px-2 py-0.5 border rounded text-xs hover:bg-accent disabled:opacity-50 shrink-0"
                  >
                    {assigning === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    Assign
                  </button>
                )}
              </div>
            );
          })}
          {data?.studies.length === 0 && (
            <p className="py-4 text-center text-muted-foreground">No studies found</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Reviewer Assignment Panel ───────────────────────────────────────────────
// Distinct from StudyAssignPanel above: that one assigns a *study* to a
// *review*; this one grants a *user* access to a review at all.

interface ReviewerAssignment {
  id: string;
  user: { id: string; name: string; email: string; role: string };
}

interface ReviewerCandidate {
  id: string;
  name: string;
  email: string;
  role: string;
}

function ReviewAssignmentPanel({ reviewId }: { reviewId: string }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const { data: assignedData, isLoading } = useQuery({
    queryKey: ["review-assignments", reviewId],
    queryFn: () => api.get<{ assignments: ReviewerAssignment[] }>(`/admin/reviews/${reviewId}/assignments`),
  });
  const assignments = assignedData?.assignments ?? [];
  const assignedIds = new Set(assignments.map((a) => a.user.id));

  const { data: candidateData, isFetching: candidatesLoading } = useQuery({
    queryKey: ["reviewer-candidates", search],
    queryFn: () =>
      api.get<{ users: ReviewerCandidate[] }>(
        `/admin/reviewer-candidates${search ? `?search=${encodeURIComponent(search)}` : ""}`
      ),
  });
  const candidates = (candidateData?.users ?? []).filter((u) => !assignedIds.has(u.id));

  const refresh = () => qc.invalidateQueries({ queryKey: ["review-assignments", reviewId] });

  const handleAdd = async (userId: string) => {
    setAdding(userId);
    setMessage(null);
    try {
      await api.post(`/admin/reviews/${reviewId}/assignments`, { userId });
      refresh();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to add reviewer");
    } finally {
      setAdding(null);
    }
  };

  const handleRemove = async (userId: string) => {
    setRemoving(userId);
    setMessage(null);
    try {
      await api.delete(`/admin/reviews/${reviewId}/assignments/${userId}`);
      refresh();
      qc.invalidateQueries({ queryKey: ["study-allocations", reviewId] });
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to remove reviewer");
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="border rounded-md p-3 bg-background space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-xs font-semibold">Reviewers assigned to this review</p>
        <p className="text-xs text-muted-foreground">Only assigned reviewers and sub-team leaders can screen it</p>
        {message && <p className="text-xs text-destructive ml-auto">{message}</p>}
      </div>

      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mx-auto" />
      ) : assignments.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No reviewers assigned yet.</p>
      ) : (
        <div className="divide-y text-xs">
          {assignments.map((a) => (
            <div key={a.id} className="flex items-center justify-between py-1.5 gap-2">
              <div>
                <span className="font-medium">{a.user.name}</span>{" "}
                <span className="text-muted-foreground">({ROLE_LABELS[a.user.role] ?? a.user.role})</span>
              </div>
              <button
                onClick={() => handleRemove(a.user.id)}
                disabled={removing === a.user.id}
                className="text-destructive hover:underline disabled:opacity-50"
              >
                {removing === a.user.id ? "Removing…" : "Remove"}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="pt-2 border-t">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search reviewers by name or email to add…"
          className="w-full px-2.5 py-1.5 border rounded-md text-xs bg-background focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {candidatesLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mx-auto mt-2" />
        ) : (
          <div className="divide-y max-h-40 overflow-y-auto text-xs mt-2">
            {candidates.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-1.5 gap-2">
                <div>
                  <span className="font-medium">{c.name}</span>{" "}
                  <span className="text-muted-foreground">({ROLE_LABELS[c.role] ?? c.role})</span>
                </div>
                <button
                  onClick={() => handleAdd(c.id)}
                  disabled={adding === c.id}
                  className="flex items-center gap-1 px-2 py-0.5 border rounded text-xs hover:bg-accent disabled:opacity-50"
                >
                  {adding === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  Add
                </button>
              </div>
            ))}
            {candidates.length === 0 && (
              <p className="py-2 text-center text-muted-foreground">
                {search ? "No matching reviewers" : "No unassigned reviewer/sub-team leader accounts"}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Study Allocation Panel ──────────────────────────────────────────────────
// Purely opt-in: narrows an assigned reviewer's queue to specific studies
// within this review. A review that never uses this behaves exactly as if
// every assigned reviewer sees the full queue.

interface StudyAllocation {
  id: string;
  study: { id: string; title: string; year: number | null; authors: string[] };
  user: { id: string; name: string };
}

function StudyAllocationPanel({ reviewId }: { reviewId: string }) {
  const qc = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [studySearch, setStudySearch] = useState("");
  const [selectedStudyIds, setSelectedStudyIds] = useState<Set<string>>(new Set());
  const [allocating, setAllocating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<{ user: { id: string; name: string }; reasoning: string }[] | null>(null);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  const { data: assignedData } = useQuery({
    queryKey: ["review-assignments", reviewId],
    queryFn: () => api.get<{ assignments: ReviewerAssignment[] }>(`/admin/reviews/${reviewId}/assignments`),
  });
  const assignedReviewers = assignedData?.assignments.map((a) => a.user) ?? [];

  const { data: allocationData, isLoading: allocationsLoading } = useQuery({
    queryKey: ["study-allocations", reviewId],
    queryFn: () => api.get<{ allocations: StudyAllocation[] }>(`/admin/reviews/${reviewId}/allocations`),
  });
  const allocations = allocationData?.allocations ?? [];

  const { data: studyData, isFetching: studiesLoading } = useQuery({
    queryKey: ["studies-for-allocate", reviewId, studySearch],
    queryFn: () =>
      api.get<{ studies: StudySummary[] }>(
        `/studies?limit=20&page=1${studySearch ? `&search=${encodeURIComponent(studySearch)}` : ""}`
      ),
  });
  // Only offer studies actually in this review - allocation narrows an
  // existing review membership, it doesn't assign the study to the review.
  const candidateStudies = (studyData?.studies ?? []).filter((s) =>
    s.studyReviews?.some((sr) => sr.reviewId === reviewId)
  );

  const toggleStudy = (id: string) => {
    setSelectedStudyIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleAllocate = async () => {
    if (!selectedUserId || selectedStudyIds.size === 0) return;
    setAllocating(true);
    setMessage(null);
    try {
      await api.post(`/admin/reviews/${reviewId}/allocations`, {
        studyIds: Array.from(selectedStudyIds),
        userId: selectedUserId,
      });
      setSelectedStudyIds(new Set());
      qc.invalidateQueries({ queryKey: ["study-allocations", reviewId] });
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to allocate studies");
    } finally {
      setAllocating(false);
    }
  };

  const firstSelectedStudy = Array.from(selectedStudyIds)[0];

  const handleSuggest = async () => {
    if (!firstSelectedStudy) return;
    setSuggesting(true);
    setSuggestions(null);
    setSuggestNote(null);
    setMessage(null);
    try {
      const result = await api.post<{
        suggestions: { user: { id: string; name: string }; reasoning: string }[];
        note?: string;
      }>(`/admin/reviews/${reviewId}/suggest-reviewer`, { studyId: firstSelectedStudy });
      setSuggestions(result.suggestions);
      setSuggestNote(result.note ?? null);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to get a suggestion");
    } finally {
      setSuggesting(false);
    }
  };

  const handleRemove = async (allocationId: string) => {
    setRemoving(allocationId);
    try {
      await api.delete(`/admin/reviews/${reviewId}/allocations/${allocationId}`);
      qc.invalidateQueries({ queryKey: ["study-allocations", reviewId] });
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to remove allocation");
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="border rounded-md p-3 bg-background space-y-3">
      <div className="flex items-center gap-2">
        <p className="text-xs font-semibold">Allocate specific studies to a reviewer</p>
        <p className="text-xs text-muted-foreground">Optional - leave unused and every assigned reviewer sees the full queue</p>
        {message && <p className="text-xs text-destructive ml-auto">{message}</p>}
      </div>

      {assignedReviewers.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">Assign at least one reviewer above before allocating studies.</p>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="px-2.5 py-1.5 border rounded-md text-xs bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select a reviewer…</option>
              {assignedReviewers.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <button
              onClick={() => void handleSuggest()}
              disabled={!firstSelectedStudy || suggesting}
              title={selectedStudyIds.size > 1 ? "Suggests based on the first selected study" : "Suggest a reviewer based on expertise"}
              className="flex items-center gap-1.5 px-2.5 py-1.5 border rounded-md text-xs font-medium hover:bg-accent disabled:opacity-50"
            >
              {suggesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Suggest reviewer
            </button>
            <button
              onClick={() => void handleAllocate()}
              disabled={!selectedUserId || selectedStudyIds.size === 0 || allocating}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {allocating ? <Loader2 className="h-3 w-3 animate-spin" /> : <ClipboardCheck className="h-3 w-3" />}
              Allocate {selectedStudyIds.size > 0 ? `${selectedStudyIds.size} ` : ""}selected
            </button>
          </div>

          {suggestNote && (
            <p className="text-xs text-muted-foreground italic">{suggestNote}</p>
          )}
          {suggestions && suggestions.length > 0 && (
            <div className="border rounded-md divide-y text-xs">
              {suggestions.map((s, i) => (
                <button
                  key={s.user.id}
                  onClick={() => setSelectedUserId(s.user.id)}
                  className={`w-full text-left px-2.5 py-1.5 hover:bg-accent ${selectedUserId === s.user.id ? "bg-accent" : ""}`}
                >
                  <span className="font-medium">{i + 1}. {s.user.name}</span>
                  <span className="text-muted-foreground"> — {s.reasoning}</span>
                </button>
              ))}
            </div>
          )}

          <input
            type="text"
            value={studySearch}
            onChange={(e) => setStudySearch(e.target.value)}
            placeholder="Search studies already in this review…"
            className="w-full px-2.5 py-1.5 border rounded-md text-xs bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {studiesLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mx-auto" />
          ) : (
            <div className="divide-y max-h-48 overflow-y-auto text-xs border rounded-md">
              {candidateStudies.map((s) => (
                <label key={s.id} className="flex items-center gap-2 py-1.5 px-2 hover:bg-accent cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedStudyIds.has(s.id)}
                    onChange={() => toggleStudy(s.id)}
                  />
                  <span className="truncate flex-1">{s.title}</span>
                  {s.year && <span className="text-muted-foreground shrink-0">{s.year}</span>}
                </label>
              ))}
              {candidateStudies.length === 0 && (
                <p className="py-2 text-center text-muted-foreground">
                  {studySearch ? "No matching studies in this review" : "No studies in this review yet"}
                </p>
              )}
            </div>
          )}
        </>
      )}

      <div className="pt-2 border-t">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
          Current allocations {allocations.length > 0 ? `(${allocations.length})` : ""}
        </p>
        {allocationsLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mx-auto" />
        ) : allocations.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">None yet.</p>
        ) : (
          <div className="divide-y text-xs">
            {allocations.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-1.5 gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate">{a.study.title}</p>
                  <p className="text-muted-foreground">→ {a.user.name}</p>
                </div>
                <button
                  onClick={() => handleRemove(a.id)}
                  disabled={removing === a.id}
                  className="text-destructive hover:underline shrink-0 disabled:opacity-50"
                >
                  {removing === a.id ? "Removing…" : "Remove"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Search Runs Panel ────────────────────────────────────────────────────────

const SEARCH_DATABASES = [
  "PUBMED", "EMBASE", "CINAHL", "WEB_OF_SCIENCE", "SCOPUS", "IEEE", "COCHRANE", "MANUAL", "OTHER",
];

function SearchRunsPanel({ reviewId, isCoordinator }: { reviewId: string; isCoordinator: boolean }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    database: "PUBMED",
    nResults: 0,
    nImported: 0,
    nDuplicates: 0,
    notes: "",
    ranAt: new Date().toISOString().slice(0, 16),
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["search-runs", reviewId],
    queryFn: () => api.get<{ runs: SearchRun[] }>(`/admin/reviews/${reviewId}/search-runs`),
  });

  const handleLog = async () => {
    setSaving(true);
    setErr(null);
    try {
      await api.post(`/admin/reviews/${reviewId}/search-runs`, {
        ...form,
        ranAt: new Date(form.ranAt).toISOString(),
      });
      await qc.invalidateQueries({ queryKey: ["search-runs", reviewId] });
      setShowForm(false);
      setForm({ database: "PUBMED", nResults: 0, nImported: 0, nDuplicates: 0, notes: "", ranAt: new Date().toISOString().slice(0, 16) });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to log run");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">Search Run Log</span>
          <span className="text-xs text-muted-foreground">Records each database search for PRISMA transparency</span>
        </div>
        {isCoordinator && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent"
          >
            <Plus className="h-3 w-3" /> {showForm ? "Cancel" : "Log search run"}
          </button>
        )}
      </div>

      {showForm && (
        <div className="mb-4 p-3 border rounded-lg bg-secondary/20 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <label className="block font-medium mb-0.5">Database</label>
              <select value={form.database} onChange={(e) => setForm((f) => ({ ...f, database: e.target.value }))}
                className="w-full px-2 py-1.5 border rounded bg-background focus:outline-none focus:ring-2 focus:ring-ring">
                {SEARCH_DATABASES.map((db) => <option key={db} value={db}>{db.replace("_", " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-medium mb-0.5">Date searched</label>
              <input type="datetime-local" value={form.ranAt} onChange={(e) => setForm((f) => ({ ...f, ranAt: e.target.value }))}
                className="w-full px-2 py-1.5 border rounded bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div>
              <label className="block font-medium mb-0.5">Total hits (n results)</label>
              <input type="number" min={0} value={form.nResults} onChange={(e) => setForm((f) => ({ ...f, nResults: parseInt(e.target.value, 10) || 0 }))}
                className="w-full px-2 py-1.5 border rounded bg-background text-center focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div>
              <label className="block font-medium mb-0.5">Imported (after dedup)</label>
              <input type="number" min={0} value={form.nImported} onChange={(e) => setForm((f) => ({ ...f, nImported: parseInt(e.target.value, 10) || 0 }))}
                className="w-full px-2 py-1.5 border rounded bg-background text-center focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div>
              <label className="block font-medium mb-0.5">Duplicates removed</label>
              <input type="number" min={0} value={form.nDuplicates} onChange={(e) => setForm((f) => ({ ...f, nDuplicates: parseInt(e.target.value, 10) || 0 }))}
                className="w-full px-2 py-1.5 border rounded bg-background text-center focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div>
              <label className="block font-medium mb-0.5">Notes (optional)</label>
              <input type="text" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Date limits 2010–2026, English only"
                className="w-full px-2 py-1.5 border rounded bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
          </div>
          {err && <p className="text-xs text-destructive">{err}</p>}
          <button onClick={handleLog} disabled={saving}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 disabled:opacity-50">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save run
          </button>
        </div>
      )}

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : (data?.runs?.length ?? 0) === 0 ? (
        <p className="text-xs text-muted-foreground italic">No search runs logged yet. Use "Log search run" after each database search.</p>
      ) : (
        <div className="border rounded-md overflow-hidden text-xs">
          <table className="w-full">
            <thead className="bg-secondary/60 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Database</th>
                <th className="text-left px-3 py-2 font-semibold">Date</th>
                <th className="text-right px-3 py-2 font-semibold">Hits</th>
                <th className="text-right px-3 py-2 font-semibold">Imported</th>
                <th className="text-right px-3 py-2 font-semibold">Dupes</th>
                <th className="text-left px-3 py-2 font-semibold">Logged by</th>
                <th className="text-left px-3 py-2 font-semibold">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data?.runs.map((run) => (
                <tr key={run.id} className="hover:bg-secondary/20">
                  <td className="px-3 py-2 font-medium">{run.database.replace("_", " ")}</td>
                  <td className="px-3 py-2 text-muted-foreground">{new Date(run.ranAt).toLocaleDateString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{run.nResults.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-green-700">{run.nImported.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{run.nDuplicates.toLocaleString()}</td>
                  <td className="px-3 py-2 text-muted-foreground">{run.runner.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{run.notes ?? "-"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-secondary/30 border-t">
              <tr>
                <td colSpan={2} className="px-3 py-2 font-semibold">Totals</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                  {(data?.runs ?? []).reduce((s, r) => s + r.nResults, 0).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums text-green-700">
                  {(data?.runs ?? []).reduce((s, r) => s + r.nImported, 0).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums text-amber-700">
                  {(data?.runs ?? []).reduce((s, r) => s + r.nDuplicates, 0).toLocaleString()}
                </td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Protocol History Panel ───────────────────────────────────────────────────

const FIELD_LABELS: Record<string, string> = {
  inclusionCriteria: "Inclusion criteria",
  exclusionCriteria: "Exclusion criteria",
  searchString: "Search string",
  additionalConstraints: "Additional AI constraints",
  pico: "Framework (PICO/PIRO/PCC)",
};

function ProtocolHistoryPanel({ reviewId }: { reviewId: string }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["review-history", reviewId],
    queryFn: () => api.get<{ entries: HistoryEntry[] }>(`/admin/reviews/${reviewId}/history`),
  });

  return (
    <div className="border-t pt-4">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Protocol Version History</span>
        <span className="text-xs text-muted-foreground">All changes to criteria, search strings, and framework data</span>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : (data?.entries?.length ?? 0) === 0 ? (
        <p className="text-xs text-muted-foreground italic">No protocol changes recorded yet. Changes to inclusion/exclusion criteria, search strings, or framework data are logged here automatically.</p>
      ) : (
        <div className="space-y-2">
          {data?.entries.map((entry) => (
            <div key={entry.id} className="border rounded-md overflow-hidden text-xs">
              <button
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/30 text-left"
                onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-primary">{FIELD_LABELS[entry.field] ?? entry.field}</span>
                  <span className="text-muted-foreground">by {entry.user.name}</span>
                  {entry.reason && (
                    <span className="italic text-muted-foreground">"{entry.reason}"</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-muted-foreground">{new Date(entry.createdAt).toLocaleString()}</span>
                  {expanded === entry.id ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </div>
              </button>
              {expanded === entry.id && (
                <div className="px-3 pb-3 border-t grid md:grid-cols-2 gap-3">
                  <div>
                    <p className="font-medium text-muted-foreground mb-1 mt-2">Previous value</p>
                    <pre className="whitespace-pre-wrap bg-red-50 border border-red-100 rounded p-2 leading-relaxed text-[11px] max-h-40 overflow-y-auto">
                      {entry.oldValue || "(empty)"}
                    </pre>
                  </div>
                  <div>
                    <p className="font-medium text-muted-foreground mb-1 mt-2">New value</p>
                    <pre className="whitespace-pre-wrap bg-green-50 border border-green-100 rounded p-2 leading-relaxed text-[11px] max-h-40 overflow-y-auto">
                      {entry.newValue || "(empty)"}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Exclusion Categories Panel ───────────────────────────────────────────────

/**
 * One reusable panel for both directions - inclusion checklist items and
 * exclusion reason codes are the same shape (a review-owned string list,
 * AI-generatable, manually editable), differing only in labels, the
 * generate endpoint, and which Review field they read/write.
 */
function CriteriaCategoriesPanel({
  reviewId, reviewType, kind, initialCategories, onSaved,
}: {
  reviewId: string; reviewType: string; kind: "inclusion" | "exclusion"; initialCategories: string[]; onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState<string[]>(initialCategories);
  const [newCat, setNewCat] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const fieldKey = kind === "inclusion" ? "inclusionCategories" : "exclusionCategories";
  const generateEndpoint = kind === "inclusion" ? "generate-inclusion-categories" : "generate-exclusion-categories";
  const title = kind === "inclusion" ? "Structured Inclusion Criteria" : "Structured Exclusion Reasons";
  const itemNounSingular = kind === "inclusion" ? "criterion" : "reason";
  const itemNounPlural = kind === "inclusion" ? "criteria" : "reasons";
  const description = kind === "inclusion"
    ? `These ${reviewType}-specific criteria appear as checkboxes when including a paper during screening, making it clear which criteria justified the decision.`
    : `These ${reviewType}-specific reason codes appear as checkboxes when excluding a paper during screening, making it faster to record why a paper is excluded and enabling auto-generation of the PRISMA exclusion table.`;
  const addPlaceholder = kind === "inclusion" ? "Add custom criterion…" : "Add custom reason…";

  const handleGenerate = async () => {
    setGenerating(true);
    setErr(null);
    try {
      const result = await api.post<Record<string, string[]>>(`/admin/reviews/${reviewId}/${generateEndpoint}`, {});
      setCategories(result[fieldKey] ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      await api.put(`/admin/reviews/${reviewId}`, { [fieldKey]: categories });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const addCategory = () => {
    const trimmed = newCat.trim();
    if (trimmed && !categories.includes(trimmed)) {
      setCategories((prev) => [...prev, trimmed]);
      setNewCat("");
    }
  };

  return (
    <div className="border-t pt-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-semibold hover:text-primary transition-colors"
      >
        <Tag className="h-4 w-4 text-muted-foreground" />
        {title}
        <span className="text-xs font-normal text-muted-foreground ml-1">
          ({categories.length} {categories.length === 1 ? itemNounSingular : itemNounPlural}) - shown in screening checkboxes
        </span>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground ml-auto" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground ml-auto" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="text-xs text-muted-foreground flex items-start gap-2">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>{description}</span>
          </div>

          <div className="flex gap-2 flex-wrap">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-md hover:bg-accent disabled:opacity-50"
            >
              {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
              Generate from criteria (AI)
            </button>
          </div>

          <div className="space-y-1.5">
            {categories.map((cat, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="flex-1 text-xs border rounded px-2 py-1 bg-secondary/30">{cat}</span>
                <button
                  onClick={() => setCategories((prev) => prev.filter((_, j) => j !== i))}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); }}}
              placeholder={addPlaceholder}
              className="flex-1 px-2 py-1.5 border rounded-md bg-background text-xs focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button onClick={addCategory} className="text-xs px-2.5 py-1 border rounded-md hover:bg-accent">
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>

          {err && <p className="text-xs text-destructive">{err}</p>}
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-xs font-medium hover:bg-primary/90 disabled:opacity-50">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save {kind === "inclusion" ? "inclusion criteria" : "exclusion reasons"}
          </button>
        </div>
      )}
    </div>
  );
}

function ActivityTab() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["audit-log"],
    queryFn: () => api.get<{ entries: AuditEntry[] }>("/admin/audit-log"),
    refetchInterval: 30_000,
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Time</th>
              <th className="text-left px-4 py-3 font-medium">Action</th>
              <th className="text-left px-4 py-3 font-medium">Entity</th>
              <th className="text-left px-4 py-3 font-medium">User</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data?.entries.map((entry) => (
              <tr key={entry.id}>
                <td className="px-4 py-2.5 text-muted-foreground text-xs">
                  {new Date(entry.createdAt).toLocaleString()}
                </td>
                <td className="px-4 py-2.5">
                  <span className="text-xs font-mono bg-secondary px-1.5 py-0.5 rounded">
                    {entry.action}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">
                  {entry.entityType && `${entry.entityType} ${entry.entityId?.slice(0, 8) ?? ""}…`}
                </td>
                <td className="px-4 py-2.5 text-xs">{entry.user?.name ?? "System"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ["reviewer-stats"],
    queryFn: () => api.get<{ users: UserRecord[] }>("/users"),
  });

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Inter-rater agreement is calculated across shared decisions on the same study, review, and screening phase.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        {data?.users
          .filter((u) => u.role === "REVIEWER" || u.role === "SUB_TEAM_LEADER" || u.role === "COORDINATOR")
          .map((user) => (
            <UserStatsCard key={user.id} user={user} />
          ))}
      </div>
    </div>
  );
}

function UserStatsCard({ user }: { user: UserRecord }) {
  const { data, isLoading } = useQuery({
    queryKey: ["user-stats", user.id],
    queryFn: () =>
      api.get<{
        totalDecisions: number;
        agreements: number;
        conflicts: number;
        agreementRate: number | null;
      }>(`/users/${user.id}/stats`),
  });

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-medium text-sm">{user.name}</p>
          <p className="text-xs text-muted-foreground">{ROLE_LABELS[user.role] ?? user.role}</p>
        </div>
        {data?.agreementRate != null && (
          <span className="text-lg font-bold text-primary">{data.agreementRate}%</span>
        )}
      </div>
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        data && (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Total decisions</dt>
            <dd className="font-medium">{data.totalDecisions}</dd>
            <dt className="text-muted-foreground">Agreements</dt>
            <dd className="font-medium text-green-600">{data.agreements}</dd>
            <dt className="text-muted-foreground">Conflicts</dt>
            <dd className="font-medium text-amber-600">{data.conflicts}</dd>
          </dl>
        )
      )}
    </div>
  );
}

// ─── Platform Settings tab ───────────────────────────────────────────────────

interface PlatformSettings {
  dedupSimilarityThreshold: number;
  dedupCandidateLimit: number;
  aiScreeningMaxTokens: number;
  aiExtractionMaxTokens: number;
  aiTemperature: number;
  screeningQueueDefaultSize: number;
  screeningQueueMaxSize: number;
  emailDigestLookbackHours: number;
  pubmedMaxResultsPerFetch: number;
  aiVisionEnabled: boolean;
  aiVisionMaxPages: number;
  aiCostDisplayEnabled: boolean;
  aiInputPricePerMTok: number;
  aiOutputPricePerMTok: number;
  aiBudgetUsd: number | null;
}

function SettingsTab() {
  const qc = useQueryClient();
  const { user: me } = useAuthStore();
  const canEdit = me?.role === "ADMIN" || me?.role === "COORDINATOR";

  const { data, isLoading } = useQuery({
    queryKey: ["platform-settings"],
    queryFn: () => api.get<{ settings: PlatformSettings; aiModel: string }>("/admin/settings"),
  });

  const [form, setForm] = useState<PlatformSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const isAdmin = me?.role === "ADMIN";
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<Record<string, number> | null>(null);
  const RESET_CONFIRM_PHRASE = "DELETE ALL REVIEW DATA";

  const handleReset = async () => {
    setResetting(true);
    setResetError(null);
    try {
      const result = await api.post<{ deleted: Record<string, number> }>("/admin/reset-review-data", {
        confirm: resetConfirmText,
      });
      setResetResult(result.deleted);
      setShowResetConfirm(false);
      setResetConfirmText("");
      await qc.invalidateQueries();
    } catch (err) {
      setResetError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  // Initialise form from fetched data (only once)
  const settings = form ?? data?.settings ?? null;

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await api.put("/admin/settings", settings);
      setSaved(true);
      setForm(null); // reset to server state
      await qc.invalidateQueries({ queryKey: ["platform-settings"] });
      // Cost display switch, prices and budget are read elsewhere too.
      await qc.invalidateQueries({ queryKey: ["public-config"] });
      await qc.invalidateQueries({ queryKey: ["ai-spend"] });
      await qc.invalidateQueries({ queryKey: ["cost-estimate"] });
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof PlatformSettings>(key: K, value: PlatformSettings[K]) => {
    setForm((f) => ({ ...(f ?? data!.settings), [key]: value }));
  };

  const isDirty = form !== null;

  if (isLoading || !settings) return <LoadingSpinner />;

  return (
    <div className="space-y-6 max-w-2xl">
      {!canEdit && (
        <p className="text-sm text-muted-foreground bg-secondary/50 border rounded-md px-3 py-2">
          You can view these settings. Only Coordinators and Admins can save changes.
        </p>
      )}

      {/* ── Deduplication ─────────────────────────────────────────────────── */}
      <SettingsSection title="Deduplication" description="Controls how aggressively incoming records are matched to existing studies.">
        <SettingRow
          label="Similarity threshold"
          description="Minimum Sørensen-Dice trigram score (0–1) to call two titles duplicates. Lower = more aggressive deduplication."
          hint={`Current: ${(settings.dedupSimilarityThreshold * 100).toFixed(0)}% (typical range 80–95%)`}
        >
          <input
            type="number" min={0.5} max={1} step={0.01}
            value={settings.dedupSimilarityThreshold}
            disabled={!canEdit}
            onChange={(e) => set("dedupSimilarityThreshold", parseFloat(e.target.value) || 0.85)}
            className="w-24 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </SettingRow>
        <SettingRow
          label="Candidate limit"
          description="Max number of existing studies fetched from the DB as deduplication candidates per import record. Larger = more accurate but slower on big databases."
          hint="Default: 500"
        >
          <input
            type="number" min={50} max={5000} step={50}
            value={settings.dedupCandidateLimit}
            disabled={!canEdit}
            onChange={(e) => set("dedupCandidateLimit", parseInt(e.target.value, 10) || 500)}
            className="w-28 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </SettingRow>
      </SettingsSection>

      {/* ── AI behaviour ─────────────────────────────────────────────────── */}
      <SettingsSection title="AI behaviour" description="Controls how Claude reasons across all AI features: screening, extraction, risk of bias, and search string generation.">
        {/* Model display (read-only) */}
        <SettingRow
          label="Active model"
          description="The Claude model used for all AI features. Set via the ANTHROPIC_MODEL environment variable. Contact your system administrator to change it."
          hint="Read-only: restart the server after changing the env var"
        >
          <span className="px-3 py-1.5 border rounded-md bg-secondary text-sm font-mono text-muted-foreground select-all">
            {data?.aiModel ?? "-"}
          </span>
        </SettingRow>
        {/* Temperature */}
        <SettingRow
          label="Temperature"
          description={
            <span>
              Controls how deterministic Claude's outputs are.{" "}
              <strong>0 (default) = fully deterministic</strong>: the same paper, criteria, and model version will always produce the same decision. This is required for PRISMA-AI reproducibility reporting.{" "}
              <strong>1 = maximum randomness</strong>: responses vary between runs. Only raise this for exploratory use; never for peer-reviewed screening.
            </span>
          }
          hint="Default: 0 (recommended for systematic reviews)"
        >
          <div className="flex items-center gap-3">
            <input
              type="range" min={0} max={1} step={0.1}
              value={settings.aiTemperature}
              disabled={!canEdit}
              onChange={(e) => set("aiTemperature", parseFloat(e.target.value))}
              className="w-32 accent-primary disabled:opacity-60"
            />
            <span className={`text-sm font-semibold tabular-nums w-8 text-center ${settings.aiTemperature === 0 ? "text-green-600" : settings.aiTemperature <= 0.3 ? "text-yellow-600" : "text-red-600"}`}>
              {settings.aiTemperature.toFixed(1)}
            </span>
            {settings.aiTemperature === 0 && <span className="text-xs text-green-600 font-medium">Reproducible</span>}
            {settings.aiTemperature > 0 && settings.aiTemperature <= 0.3 && <span className="text-xs text-yellow-600 font-medium">Slight variation</span>}
            {settings.aiTemperature > 0.3 && <span className="text-xs text-red-600 font-medium">Non-reproducible</span>}
          </div>
        </SettingRow>
        {/* Token limits */}
        <SettingRow
          label="Screening max tokens"
          description="Max tokens in Claude's screening response. The structured JSON output is short; 1024 is generous."
          hint="Default: 1024 · Typical range 512–2048"
        >
          <input
            type="number" min={256} max={8192} step={128}
            value={settings.aiScreeningMaxTokens}
            disabled={!canEdit}
            onChange={(e) => set("aiScreeningMaxTokens", parseInt(e.target.value, 10) || 1024)}
            className="w-28 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </SettingRow>
        <SettingRow
          label="Extraction max tokens"
          description="Max tokens in Claude's extraction pre-fill response. The JSON can be large; 2048 covers most papers."
          hint="Default: 2048 · Typical range 1024–4096"
        >
          <input
            type="number" min={512} max={8192} step={256}
            value={settings.aiExtractionMaxTokens}
            disabled={!canEdit}
            onChange={(e) => set("aiExtractionMaxTokens", parseInt(e.target.value, 10) || 2048)}
            className="w-28 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </SettingRow>
        <p className="text-xs text-muted-foreground pt-1">
          <strong>Note:</strong> Per-review character limits (how much text is sent to the AI) are configured individually in the Reviews tab.
        </p>
      </SettingsSection>

      {/* ── Screening queue ───────────────────────────────────────────────── */}
      <SettingsSection title="Screening queue" description="Controls batch sizes presented to reviewers.">
        <SettingRow
          label="Default queue size"
          description="Number of studies returned per queue fetch when the reviewer doesn't specify a limit."
          hint="Default: 20"
        >
          <input
            type="number" min={5} max={200} step={5}
            value={settings.screeningQueueDefaultSize}
            disabled={!canEdit}
            onChange={(e) => set("screeningQueueDefaultSize", parseInt(e.target.value, 10) || 20)}
            className="w-24 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </SettingRow>
        <SettingRow
          label="Maximum queue size"
          description="Hard cap on the number of studies a reviewer can request per call. Prevents accidental huge fetches."
          hint="Default: 50"
        >
          <input
            type="number" min={10} max={500} step={10}
            value={settings.screeningQueueMaxSize}
            disabled={!canEdit}
            onChange={(e) => set("screeningQueueMaxSize", parseInt(e.target.value, 10) || 50)}
            className="w-24 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </SettingRow>
      </SettingsSection>

      {/* ── AI costs ──────────────────────────────────────────────────────── */}
      <SettingsSection
        title="AI costs"
        description="Every AI call is recorded with its token counts. Costs are worked out from the prices below, so keep them in line with Anthropic's published price for the active model."
      >
        <SettingRow
          label="Show costs next to AI actions"
          description="Shows an estimated cost before AI buttons are clicked (pre-fill, image analysis, quality appraisal) and the actual cost after each AI answer. Useful while testing; switch off to hide costs from reviewers."
          hint="Default: off"
        >
          <input
            type="checkbox"
            checked={settings.aiCostDisplayEnabled}
            disabled={!canEdit}
            onChange={(e) => set("aiCostDisplayEnabled", e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
        </SettingRow>
        <SettingRow
          label="Price per million input tokens (USD)"
          description={<span>What Anthropic charges for text and images sent to the model. Sonnet 4.6: $3. See <a href="https://www.anthropic.com/pricing#api" target="_blank" rel="noreferrer" className="underline">anthropic.com/pricing</a>.</span>}
          hint="Default: 3"
        >
          <input
            type="number" min={0} max={1000} step={0.01}
            value={settings.aiInputPricePerMTok}
            disabled={!canEdit}
            onChange={(e) => set("aiInputPricePerMTok", parseFloat(e.target.value) || 0)}
            className="w-24 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </SettingRow>
        <SettingRow
          label="Price per million output tokens (USD)"
          description="What Anthropic charges for the model's answers. Sonnet 4.6: $15."
          hint="Default: 15"
        >
          <input
            type="number" min={0} max={1000} step={0.01}
            value={settings.aiOutputPricePerMTok}
            disabled={!canEdit}
            onChange={(e) => set("aiOutputPricePerMTok", parseFloat(e.target.value) || 0)}
            className="w-24 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </SettingRow>
        <SettingRow
          label="Budget (USD)"
          description="Optional. The running total below is shown against it, for example the credit loaded on the Anthropic account. Leave empty for no budget."
          hint="Empty = no budget"
        >
          <input
            type="number" min={0} step={1}
            value={settings.aiBudgetUsd ?? ""}
            disabled={!canEdit}
            onChange={(e) => set("aiBudgetUsd", e.target.value === "" ? null : Math.max(0, parseFloat(e.target.value) || 0))}
            className="w-28 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </SettingRow>
        <SettingRow
          label="Send figure pages automatically during AI pre-fill"
          description="Off: images reach the AI only when a reviewer picks them in the extraction image gallery. On: every AI pre-fill also sends up to the number of figure pages below, whether or not they are useful, at roughly 1,600 input tokens per page."
          hint="Default: off"
        >
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.aiVisionEnabled}
              disabled={!canEdit}
              onChange={(e) => set("aiVisionEnabled", e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            <input
              type="number" min={0} max={20} step={1}
              value={settings.aiVisionMaxPages}
              disabled={!canEdit || !settings.aiVisionEnabled}
              onChange={(e) => set("aiVisionMaxPages", parseInt(e.target.value, 10) || 0)}
              className="w-16 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
            />
            <span className="text-xs text-muted-foreground">pages at most</span>
          </div>
        </SettingRow>
        <AiSpendPanel isAdmin={isAdmin} />
      </SettingsSection>

      {/* ── Background jobs ───────────────────────────────────────────────── */}
      <SettingsSection title="Background jobs" description="Scheduled task configuration. Schedule patterns are set via environment variables; these control operational parameters.">
        <SettingRow
          label="Email digest lookback (hours)"
          description="How many hours back the digest email counts 'new records'. Matches your email schedule, e.g. 24 for daily, 168 for weekly."
          hint="Default: 24"
        >
          <input
            type="number" min={1} max={720} step={1}
            value={settings.emailDigestLookbackHours}
            disabled={!canEdit}
            onChange={(e) => set("emailDigestLookbackHours", parseInt(e.target.value, 10) || 24)}
            className="w-24 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
          <span className="text-xs text-muted-foreground self-center">hours</span>
        </SettingRow>
        <SettingRow
          label="PubMed max results per fetch"
          description="Maximum number of records fetched from each PubMed saved search per poll cycle. Increase for comprehensive reviews with high publication rates."
          hint="Default: 500 · PubMed allows up to 10,000"
        >
          <input
            type="number" min={50} max={10000} step={50}
            value={settings.pubmedMaxResultsPerFetch}
            disabled={!canEdit}
            onChange={(e) => set("pubmedMaxResultsPerFetch", parseInt(e.target.value, 10) || 500)}
            className="w-28 px-2 py-1.5 border rounded-md bg-background text-sm text-center focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </SettingRow>
      </SettingsSection>

      {/* Save bar */}
      {canEdit && (
        <div className="flex items-center gap-3 pt-2 border-t">
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save settings
          </button>
          {isDirty && !saving && (
            <button
              onClick={() => setForm(null)}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Reset
            </button>
          )}
          {saved && <span className="text-sm text-green-700">Settings saved ✓</span>}
          {saveError && <span className="text-sm text-destructive">{saveError}</span>}
          {isDirty && !saved && !saving && (
            <span className="text-xs text-amber-600">Unsaved changes</span>
          )}
        </div>
      )}

      {/* Danger zone — ADMIN only */}
      {isAdmin && (
        <div className="border border-destructive/40 rounded-lg p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-destructive">Danger zone</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Irreversible actions. Use these only when you mean to.
            </p>
          </div>
          <div className="flex items-start justify-between gap-4 pt-2 border-t">
            <div>
              <p className="text-sm font-medium">Reset all review data</p>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                Permanently deletes every study, screening decision, extraction, risk-of-bias assessment,
                reviewer assignment, study allocation, search run, and the audit log. Review protocols
                (criteria, PICO, search strings), extraction subtopics, user accounts, filter presets, and
                platform settings are kept exactly as they are. Use this to start a clean pilot before
                onboarding real reviewers.
              </p>
            </div>
            <button
              onClick={() => { setShowResetConfirm(true); setResetConfirmText(""); setResetError(null); }}
              className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 border border-destructive text-destructive rounded-md text-xs font-medium hover:bg-destructive/10"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Reset…
            </button>
          </div>
          {resetResult && (
            <p className="text-xs text-green-700 pt-1">
              Done. Deleted {resetResult.studies} studies, {resetResult.decisions} decisions, {resetResult.extractions} extractions,
              and related records. Reviews, topics, users, presets, and settings were kept.
            </p>
          )}
        </div>
      )}

      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md bg-card border rounded-xl shadow-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
              <h3 className="font-semibold text-base">Reset all review data?</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              This permanently deletes every study, decision, extraction, and related record across
              every review. This cannot be undone. Review protocols, users, and settings are kept.
            </p>
            <p className="text-xs text-muted-foreground mb-1.5">
              Type <span className="font-mono font-semibold text-foreground">{RESET_CONFIRM_PHRASE}</span> to confirm.
            </p>
            <input
              type="text"
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              className="w-full px-2.5 py-1.5 border rounded-md bg-background text-sm font-mono focus:outline-none focus:ring-2 focus:ring-destructive mb-2"
              autoFocus
            />
            {resetError && <p className="text-xs text-destructive mb-2">{resetError}</p>}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="text-xs px-3 py-1.5 border rounded-md hover:bg-accent"
              >
                Cancel
              </button>
              <button
                onClick={() => void handleReset()}
                disabled={resetConfirmText !== RESET_CONFIRM_PHRASE || resetting}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 disabled:opacity-50"
              >
                {resetting && <Loader2 className="h-3 w-3 animate-spin" />}
                Delete everything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-secondary/40 border-b">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <div className="p-4 space-y-5">{children}</div>
    </div>
  );
}

function SettingRow({
  label,
  description,
  hint,
  children,
}: {
  label: string;
  description: string | React.ReactNode;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div className="flex-1">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        {hint && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{hint}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">{children}</div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

/**
 * The single quality appraisal tool used for every paper in a review. Editable
 * by coordinators and by the sub-team leader of this review; saved on change.
 */
function QualityToolControl({ reviewId, value, canEdit, onSaved }: {
  reviewId: string;
  value: string | null;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = QUALITY_TOOL_OPTIONS.find((o) => o.value === (value ?? ""))?.label ?? value;
  if (!canEdit) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-secondary border font-medium" title="Quality appraisal tool for this review">
        Quality: {label}
      </span>
    );
  }
  return (
    <label className="flex items-center gap-1.5 text-xs" title="Used for every paper in this review">
      <span className="font-medium">Quality appraisal tool:</span>
      <select
        value={value ?? ""}
        disabled={saving}
        onChange={async (e) => {
          setSaving(true);
          setError(null);
          try {
            await api.patch(`/admin/reviews/${reviewId}/quality-tool`, { qualityTool: e.target.value || null });
            onSaved();
          } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save the tool.");
          } finally {
            setSaving(false);
          }
        }}
        className="px-1.5 py-0.5 border rounded-md bg-background text-xs focus:outline-none focus:ring-2 focus:ring-ring max-w-[16rem]"
      >
        {QUALITY_TOOL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {saving && <Loader2 className="h-3 w-3 animate-spin" />}
      {error && <span className="text-destructive">{error}</span>}
    </label>
  );
}

// ─── AI spend ────────────────────────────────────────────────────────────────

interface SpendRow {
  key: string | null;
  label: string;
  calls: number;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
}

interface AiSpend {
  since: string | null;
  budgetUsd: number | null;
  total: SpendRow;
  allTime: { calls: number; costUsd: number };
  byCategory: SpendRow[];
  byReview: SpendRow[];
  byUser: SpendRow[];
}

const SPEND_CATEGORY_LABELS: Record<string, string> = {
  SCREENING_TITLE_ABSTRACT: "Title and abstract screening",
  SCREENING_FULL_TEXT: "Full-text screening",
  EXTRACTION: "Data extraction pre-fill",
  IMAGE_ANALYSIS: "Image analysis",
  QUALITY_APPRAISAL: "Quality appraisal",
  SEARCH_STRINGS: "Search strings",
  REVIEW_SETUP: "Review setup (criteria checklists)",
  REVIEWER_MATCHING: "Reviewer suggestions",
};

/**
 * Running total of AI spend since the last reset, against the budget, broken
 * down by stage, review and person. Reset only moves the start date: the
 * underlying record of every call is kept.
 */
function AiSpendPanel({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const [view, setView] = useState<"byCategory" | "byReview" | "byUser">("byCategory");
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["ai-spend"],
    queryFn: () => api.get<AiSpend>("/admin/ai-usage"),
  });

  const reset = async () => {
    setResetting(true);
    setError(null);
    try {
      await api.post("/admin/ai-usage/reset", {});
      await qc.invalidateQueries({ queryKey: ["ai-spend"] });
      setConfirmReset(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reset the running total.");
    } finally {
      setResetting(false);
    }
  };

  if (isLoading || !data) return <div className="py-3"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;

  const usd = (n: number) => (n >= 1 || n === 0 ? `$${n.toFixed(2)}` : n >= 0.01 ? `$${n.toFixed(3)}` : `$${n.toFixed(4)}`);
  const budget = data.budgetUsd;
  const pct = budget && budget > 0 ? Math.min(100, (data.total.costUsd / budget) * 100) : null;
  const rows = data[view].map((r) => ({ ...r, label: view === "byCategory" ? SPEND_CATEGORY_LABELS[r.label] ?? r.label : r.label }));

  return (
    <div className="pt-3 space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium">
            Running total: {usd(data.total.costUsd)}
            {budget != null && <span className="text-muted-foreground font-normal"> of {usd(budget)} budget</span>}
          </p>
          <p className="text-xs text-muted-foreground">
            {data.total.calls.toLocaleString()} AI {data.total.calls === 1 ? "call" : "calls"}{" "}
            {data.since ? `since ${new Date(data.since).toLocaleString()}` : "since tracking began"}.
            {" "}All time: {usd(data.allTime.costUsd)} over {data.allTime.calls.toLocaleString()} {data.allTime.calls === 1 ? "call" : "calls"}.
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setConfirmReset(true)} className="text-xs px-2.5 py-1 border rounded-md hover:bg-accent">
            Reset running total
          </button>
        )}
      </div>
      {pct != null && (
        <div className="h-2 rounded-full bg-muted overflow-hidden" title={`${pct.toFixed(0)}% of the budget used`}>
          <div className={`h-full ${pct >= 90 ? "bg-destructive" : pct >= 70 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
        </div>
      )}

      <div className="flex gap-1 text-xs">
        {([["byCategory", "By stage"], ["byReview", "By review"], ["byUser", "By person"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={`px-2.5 py-1 rounded-md border ${view === key ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No AI calls recorded in this period.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground border-b">
              <th className="py-1.5 font-medium">{view === "byCategory" ? "Stage" : view === "byReview" ? "Review" : "Person"}</th>
              <th className="py-1.5 font-medium text-right">Calls</th>
              <th className="py-1.5 font-medium text-right">Tokens in / out</th>
              <th className="py-1.5 font-medium text-right">Cost</th>
              <th className="py-1.5 font-medium text-right">Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key ?? r.label} className="border-b last:border-0">
                <td className="py-1.5">{r.label}</td>
                <td className="py-1.5 text-right tabular-nums">{r.calls.toLocaleString()}</td>
                <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                  {r.inputTokens.toLocaleString()} / {r.outputTokens.toLocaleString()}
                </td>
                <td className="py-1.5 text-right tabular-nums font-medium">{usd(r.costUsd)}</td>
                <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                  {data.total.costUsd > 0 ? `${Math.round((r.costUsd / data.total.costUsd) * 100)}%` : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-[11px] text-muted-foreground">
        Each call is costed at the prices set when it was made. Calls from before this tracking existed are not included.
        Anthropic's console remains the record of what is actually billed.
      </p>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <ConfirmDialog
        open={confirmReset}
        title="Reset the running total?"
        body="The running total starts again from zero now. The record of every past AI call is kept, and the all-time total does not change. Use this when you load new credit, for example."
        confirmLabel={resetting ? "Resetting…" : "Reset running total"}
        onConfirm={() => void reset()}
        onCancel={() => setConfirmReset(false)}
      />
    </div>
  );
}
